import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ArchitectureRoundedIcon from "@mui/icons-material/ArchitectureRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import SquareFootRoundedIcon from "@mui/icons-material/SquareFootRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

/* ============================================================
   MESYAT ARSAPRO / PANEL ARSA PAYI
   Tek dosyalık kurumsal frontend

   Backend kökü:
   /api/yapipay

   Beklenen ana uçlar:
   GET    /api/yapipay/projects
   GET    /api/yapipay/projects/:id
   POST   /api/yapipay/projects
   PUT    /api/yapipay/projects/:id
   DELETE /api/yapipay/projects/:id
   POST   /api/yapipay/projects/:id/calculate
   POST   /api/yapipay/projects/:id/report
============================================================ */

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.haymestasarim.com/api";

const YAPIPAY_API = `${API_BASE}/yapipay`;

const COLORS = {
  blue: "#0B4F8A",
  blueDark: "#07365E",
  blueDeep: "#052844",
  blueLight: "#EAF4FF",
  orange: "#F97316",
  orangeDark: "#C9540D",
  orangeLight: "#FFF1E7",
  green: "#16A34A",
  greenLight: "#EAF8EF",
  red: "#DC2626",
  redLight: "#FDECEC",
  amber: "#D97706",
  amberLight: "#FFF5DD",
  purple: "#7C3AED",
  purpleLight: "#F3ECFF",
  cyan: "#0891B2",
  cyanLight: "#E6F8FC",
  background: "#F4F7FB",
  surface: "#FFFFFF",
  text: "#172033",
  muted: "#64748B",
  border: "rgba(15, 23, 42, 0.10)",
};

const PARTY_COLORS = [
  { main: "#2563EB", light: "#DBEAFE" },
  { main: "#7C3AED", light: "#EDE9FE" },
  { main: "#DB2777", light: "#FCE7F3" },
  { main: "#0891B2", light: "#CFFAFE" },
  { main: "#16A34A", light: "#DCFCE7" },
  { main: "#D97706", light: "#FEF3C7" },
  { main: "#475569", light: "#E2E8F0" },
];

const TABS = [
  { key: "overview", label: "Genel Bakış", icon: AssessmentRoundedIcon },
  { key: "parcel", label: "Arsa ve Tapu", icon: HomeWorkRoundedIcon },
  { key: "zoning", label: "İmar ve Kitle", icon: ArchitectureRoundedIcon },
  { key: "building", label: "Kat ve Daireler", icon: ApartmentRoundedIcon },
  { key: "shares", label: "Hissedarlar", icon: GroupsRoundedIcon },
  { key: "allocation", label: "Paylaşım", icon: SwapHorizRoundedIcon },
  { key: "feasibility", label: "Fizibilite", icon: CalculateRoundedIcon },
  { key: "report", label: "Rapor", icon: DescriptionRoundedIcon },
];

const BUILDING_ORDERS = [
  { value: "detached", label: "Ayrık nizam" },
  { value: "block", label: "Blok nizam" },
  { value: "attached", label: "Bitişik nizam" },
  { value: "mass", label: "Kitle nizamı" },
  { value: "other", label: "Diğer" },
];

const PROJECT_STATUSES = [
  { value: "draft", label: "Taslak" },
  { value: "analysis", label: "Fizibilite" },
  { value: "offer", label: "Teklif" },
  { value: "agreement", label: "Anlaşma" },
  { value: "approved", label: "Onaylandı" },
  { value: "archived", label: "Arşiv" },
];

const FLOOR_TYPES = [
  { value: "basement", label: "Bodrum" },
  { value: "ground", label: "Zemin" },
  { value: "normal", label: "Normal kat" },
  { value: "attic", label: "Çatı katı" },
  { value: "terrace", label: "Teras" },
  { value: "other", label: "Diğer" },
];

const UNIT_TYPES = [
  "1+0",
  "1+1",
  "2+1",
  "3+1",
  "4+1",
  "5+1",
  "Dubleks",
  "Dükkan",
  "Ofis",
  "Depo",
  "Otopark",
  "Diğer",
];

const FACADES = [
  "Kuzey",
  "Güney",
  "Doğu",
  "Batı",
  "Kuzeydoğu",
  "Kuzeybatı",
  "Güneydoğu",
  "Güneybatı",
  "Ön",
  "Arka",
];

const CURRENCIES = ["TRY", "EUR", "USD", "CHF", "GBP"];

const tokenHeaders = () => {
  const token = localStorage.getItem("token") || "";
  const kurumId =
    localStorage.getItem("kurum_id") ||
    localStorage.getItem("institution_id") ||
    "";

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(kurumId ? { "X-Kurum-Id": kurumId } : {}),
  };
};

const uid = (prefix = "row") =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const today = () => new Date().toISOString().slice(0, 10);

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === "") return 0;

  let text = String(value).trim().replace(/\s/g, "");
  if (text.includes(",") && text.includes(".")) {
    if (text.lastIndexOf(",") > text.lastIndexOf(".")) {
      text = text.replace(/\./g, "").replace(",", ".");
    } else {
      text = text.replace(/,/g, "");
    }
  } else if (text.includes(",")) {
    text = text.replace(",", ".");
  }

  const result = Number(text);
  return Number.isFinite(result) ? result : 0;
};

const asRatio = (value) => {
  const number = toNumber(value);
  if (number <= 0) return 0;
  return number > 1 ? number / 100 : number;
};

const round = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round((toNumber(value) + Number.EPSILON) * factor) / factor;
};

const clamp = (value, min, max) => Math.min(Math.max(toNumber(value), min), max);

const formatNumber = (value, digits = 2) =>
  new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(toNumber(value));

const formatArea = (value) => `${formatNumber(value)} m²`;

const formatPercent = (value) => `%${formatNumber(value)}`;

const formatMoney = (value, currency = "TRY") =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: currency || "TRY",
    maximumFractionDigits: 0,
  }).format(toNumber(value));

const statusLabel = (value) =>
  PROJECT_STATUSES.find((item) => item.value === value)?.label || value || "Taslak";

const positiveMin = (...values) => {
  const positives = values.map(toNumber).filter((value) => value > 0);
  return positives.length ? Math.min(...positives) : 0;
};

const sumBy = (items, selector) =>
  (items || []).reduce((total, item, index) => total + toNumber(selector(item, index)), 0);

function emptyShareholder(index = 0) {
  return {
    client_id: uid("shareholder"),
    id: null,
    full_name: "",
    identity_no: "",
    phone: "",
    share_numerator: "",
    share_denominator: "",
    manual_percent: "",
    color_index: index % PARTY_COLORS.length,
    notes: "",
  };
}

function emptyFloor(order = 1) {
  return {
    client_id: uid("floor"),
    id: null,
    name: `${order}. Kat`,
    floor_no: order,
    floor_type: order === 0 ? "ground" : "normal",
    closed_area: "",
    emsal_area: "",
    common_area: "",
    exempt_area: "",
    unit_count: "",
    notes: "",
  };
}

function emptyUnit(index = 1) {
  return {
    client_id: uid("unit"),
    id: null,
    unit_no: String(index),
    floor_client_id: "",
    floor_name: "",
    unit_type: "2+1",
    usage_type: "residential",
    facade: "",
    net_area: "",
    gross_area: "",
    land_share_numerator: "",
    land_share_denominator: "",
    price_per_m2: "",
    value_coefficient: "1",
    manual_value: "",
    allocation_party: "unassigned",
    allocation_splits: [],
    notes: "",
  };
}

function emptyProject() {
  return {
    id: null,
    version: null,
    project_no: "",
    project_name: "",
    status: "draft",
    report_date: today(),
    customer_name: "",
    architect_name: "",
    contractor_company: "",
    description: "",
    parcel: {
      city: "",
      district: "",
      neighborhood: "",
      block_no: "",
      parcel_no: "",
      sheet_no: "",
      address: "",
      title_area: "",
      deduction_area: "",
      existing_building_area: "",
      existing_unit_count: "",
      title_type: "",
      title_notes: "",
    },
    zoning: {
      zoning_document_date: "",
      zoning_document_no: "",
      plan_scale: "",
      building_order: "detached",
      taks: "",
      kaks: "",
      municipal_floor_count: "",
      hmax: "",
      parcel_width: "",
      parcel_depth: "",
      front_setback: "",
      rear_setback: "",
      left_setback: "",
      right_setback: "",
      block_width: "",
      block_depth: "",
      manual_footprint_area: "",
      manual_emsal_area: "",
      road_level_difference: "",
      basement_count: "",
      ground_floor_count: "1",
      normal_floor_count: "",
      attic_floor_count: "",
      plan_notes: "",
      verification_status: "estimated",
    },
    structure: {
      planned_floor_count: "",
      units_per_floor: "",
      typical_floor_closed_area: "",
      common_area_rate: "",
      net_gross_rate: "",
      distributable_area_override: "",
    },
    agreement: {
      contractor_name: "",
      contractor_rate: "50",
      allocation_basis: "value",
      common_area_rule: "included",
      currency: "TRY",
      agreement_date: "",
      notes: "",
    },
    costs: {
      construction_cost_per_m2: "",
      demolition_cost: "",
      project_cost: "",
      permit_cost: "",
      tax_fee_cost: "",
      finance_cost: "",
      marketing_cost: "",
      other_cost: "",
      contingency_rate: "",
      average_sales_price_per_m2: "",
    },
    shareholders: [],
    floors: [],
    units: [],
    created_at: null,
    updated_at: null,
  };
}

function hydrateProject(raw) {
  const base = emptyProject();
  const source = raw?.project || raw?.data || raw || {};

  return {
    ...base,
    ...source,
    id: source.id || source.project_id || null,
    parcel: { ...base.parcel, ...(source.parcel || {}) },
    zoning: { ...base.zoning, ...(source.zoning || {}) },
    structure: { ...base.structure, ...(source.structure || {}) },
    agreement: { ...base.agreement, ...(source.agreement || {}) },
    costs: { ...base.costs, ...(source.costs || {}) },
    shareholders: (source.shareholders || []).map((item, index) => ({
      ...emptyShareholder(index),
      ...item,
      client_id: item.client_id || item.id || uid("shareholder"),
      color_index:
        item.color_index === null || item.color_index === undefined
          ? index % PARTY_COLORS.length
          : item.color_index,
    })),
    floors: (source.floors || []).map((item, index) => ({
      ...emptyFloor(index + 1),
      ...item,
      client_id: item.client_id || item.id || uid("floor"),
    })),
    units: (source.units || []).map((item, index) => ({
      ...emptyUnit(index + 1),
      ...item,
      client_id: item.client_id || item.id || uid("unit"),
      allocation_splits: Array.isArray(item.allocation_splits)
        ? item.allocation_splits
        : [],
    })),
  };
}

function shareholderPercent(item) {
  const numerator = toNumber(item.share_numerator);
  const denominator = toNumber(item.share_denominator);
  if (numerator > 0 && denominator > 0) return (numerator / denominator) * 100;
  return clamp(item.manual_percent, 0, 100);
}

function unitValue(unit, fallbackPricePerM2 = 0) {
  const manual = toNumber(unit.manual_value);
  if (manual > 0) return manual;
  const gross = toNumber(unit.gross_area);
  const price = toNumber(unit.price_per_m2) || toNumber(fallbackPricePerM2);
  const coefficient = toNumber(unit.value_coefficient) || 1;
  return gross * price * coefficient;
}

function unitPartyRatio(unit, partyId) {
  if (String(unit.allocation_party) === String(partyId)) return 1;
  if (unit.allocation_party !== "split") return 0;
  const percent = sumBy(
    (unit.allocation_splits || []).filter(
      (split) => String(split.party_id) === String(partyId)
    ),
    (split) => split.percent
  );
  return clamp(percent / 100, 0, 1);
}

function unitSplitTotal(unit) {
  return sumBy(unit.allocation_splits || [], (split) => split.percent);
}

function calculateProject(project) {
  const parcel = project.parcel || {};
  const zoning = project.zoning || {};
  const structure = project.structure || {};
  const agreement = project.agreement || {};
  const costs = project.costs || {};
  const floors = project.floors || [];
  const units = project.units || [];
  const shareholders = project.shareholders || [];

  const titleArea = toNumber(parcel.title_area);
  const deductionArea = toNumber(parcel.deduction_area);
  const netParcelArea = Math.max(0, titleArea - deductionArea);

  const parcelWidth = toNumber(zoning.parcel_width);
  const parcelDepth = toNumber(zoning.parcel_depth);
  const usableWidth = Math.max(
    0,
    parcelWidth - toNumber(zoning.left_setback) - toNumber(zoning.right_setback)
  );
  const usableDepth = Math.max(
    0,
    parcelDepth - toNumber(zoning.front_setback) - toNumber(zoning.rear_setback)
  );
  const setbackFootprint = usableWidth > 0 && usableDepth > 0 ? usableWidth * usableDepth : 0;
  const taksFootprint = netParcelArea * asRatio(zoning.taks);
  const blockFootprint =
    toNumber(zoning.block_width) > 0 && toNumber(zoning.block_depth) > 0
      ? toNumber(zoning.block_width) * toNumber(zoning.block_depth)
      : 0;
  const manualFootprint = toNumber(zoning.manual_footprint_area);
  const legalFootprintLimit = positiveMin(taksFootprint, setbackFootprint, blockFootprint);
  const projectedFootprint =
    manualFootprint || blockFootprint || legalFootprintLimit || taksFootprint || setbackFootprint;

  const emsalLimit =
    toNumber(zoning.manual_emsal_area) || netParcelArea * toNumber(zoning.kaks);
  const municipalFloorCount = toNumber(zoning.municipal_floor_count);
  const plannedFloorCount =
    toNumber(structure.planned_floor_count) ||
    toNumber(zoning.basement_count) +
      toNumber(zoning.ground_floor_count) +
      toNumber(zoning.normal_floor_count) +
      toNumber(zoning.attic_floor_count);

  const floorClosedArea = sumBy(floors, (floor) => floor.closed_area);
  const floorEmsalArea = sumBy(floors, (floor) => floor.emsal_area);
  const floorCommonArea = sumBy(floors, (floor) => floor.common_area);
  const floorExemptArea = sumBy(floors, (floor) => floor.exempt_area);
  const estimatedClosedArea =
    projectedFootprint > 0 && plannedFloorCount > 0 ? projectedFootprint * plannedFloorCount : 0;
  const totalClosedArea =
    floorClosedArea ||
    toNumber(structure.typical_floor_closed_area) * plannedFloorCount ||
    estimatedClosedArea;
  const totalEmsalArea = floorEmsalArea || Math.max(0, totalClosedArea - floorExemptArea);

  const unitGrossArea = sumBy(units, (unit) => unit.gross_area);
  const unitNetArea = sumBy(units, (unit) => unit.net_area);
  const estimatedCommon = totalClosedArea * asRatio(structure.common_area_rate);
  const distributableArea =
    toNumber(structure.distributable_area_override) ||
    unitGrossArea ||
    Math.max(0, totalClosedArea - (floorCommonArea || estimatedCommon));

  const averagePrice = toNumber(costs.average_sales_price_per_m2);
  const totalProjectValue = units.length
    ? sumBy(units, (unit) => unitValue(unit, averagePrice))
    : distributableArea * averagePrice;

  const contractorRate = clamp(agreement.contractor_rate, 0, 100);
  const ownersRate = 100 - contractorRate;
  const contractorAreaEntitlement = distributableArea * (contractorRate / 100);
  const ownersAreaEntitlement = distributableArea - contractorAreaEntitlement;
  const contractorValueEntitlement = totalProjectValue * (contractorRate / 100);
  const ownersValueEntitlement = totalProjectValue - contractorValueEntitlement;

  const totalSharePercent = sumBy(shareholders, shareholderPercent);
  const contractorAssignedUnits = units.filter(
    (unit) => unitPartyRatio(unit, "contractor") > 0
  );
  const invalidSplitUnits = units.filter(
    (unit) =>
      unit.allocation_party === "split" &&
      Math.abs(unitSplitTotal(unit) - 100) > 0.01
  );
  const unassignedUnits = units.filter(
    (unit) =>
      !unit.allocation_party ||
      unit.allocation_party === "unassigned" ||
      (unit.allocation_party === "split" && unitSplitTotal(unit) <= 0)
  );
  const contractorActualArea = sumBy(
    units,
    (unit) => toNumber(unit.gross_area) * unitPartyRatio(unit, "contractor")
  );
  const contractorActualValue = sumBy(
    units,
    (unit) => unitValue(unit, averagePrice) * unitPartyRatio(unit, "contractor")
  );

  const partyResults = shareholders.map((shareholder) => {
    const percent = shareholderPercent(shareholder);
    const ratio = percent / 100;
    const assignedUnits = units.filter(
      (unit) => unitPartyRatio(unit, shareholder.client_id) > 0
    );
    const actualArea = sumBy(
      units,
      (unit) =>
        toNumber(unit.gross_area) * unitPartyRatio(unit, shareholder.client_id)
    );
    const actualValue = sumBy(
      units,
      (unit) =>
        unitValue(unit, averagePrice) * unitPartyRatio(unit, shareholder.client_id)
    );
    const entitledArea = ownersAreaEntitlement * ratio;
    const entitledValue = ownersValueEntitlement * ratio;

    return {
      shareholder,
      percent,
      assignedUnits,
      actualArea,
      actualValue,
      entitledArea,
      entitledValue,
      areaDifference: actualArea - entitledArea,
      valueDifference: actualValue - entitledValue,
    };
  });

  const costBaseArea = totalClosedArea || totalEmsalArea;
  const directConstructionCost = costBaseArea * toNumber(costs.construction_cost_per_m2);
  const fixedCosts =
    toNumber(costs.demolition_cost) +
    toNumber(costs.project_cost) +
    toNumber(costs.permit_cost) +
    toNumber(costs.tax_fee_cost) +
    toNumber(costs.finance_cost) +
    toNumber(costs.marketing_cost) +
    toNumber(costs.other_cost);
  const preContingencyCost = directConstructionCost + fixedCosts;
  const contingencyCost = preContingencyCost * asRatio(costs.contingency_rate);
  const totalCost = preContingencyCost + contingencyCost;
  const contractorRevenue = contractorActualValue || contractorValueEntitlement;
  const estimatedProfit = contractorRevenue - totalCost;
  const profitMargin = contractorRevenue > 0 ? (estimatedProfit / contractorRevenue) * 100 : 0;
  const returnOnCost = totalCost > 0 ? (estimatedProfit / totalCost) * 100 : 0;
  const breakEvenContractorRate = totalProjectValue > 0 ? (totalCost / totalProjectValue) * 100 : 0;

  const warnings = [];
  if (!project.project_name?.trim()) warnings.push("Proje adı girilmedi.");
  if (titleArea <= 0) warnings.push("Tapu arsa alanı girilmedi.");
  if (netParcelArea <= 0 && titleArea > 0)
    warnings.push("Terk/kayıp alanı tapu alanına eşit veya daha büyük.");
  if (!asRatio(zoning.taks)) warnings.push("TAKS bilgisi girilmedi.");
  if (!toNumber(zoning.kaks) && !toNumber(zoning.manual_emsal_area))
    warnings.push("KAKS/emsal bilgisi girilmedi.");
  if (!municipalFloorCount) warnings.push("Belediyenin izin verdiği kat adedi girilmedi.");
  if (municipalFloorCount > 0 && plannedFloorCount > municipalFloorCount)
    warnings.push("Planlanan kat adedi belediye kat sınırını aşıyor.");
  if (legalFootprintLimit > 0 && projectedFootprint > legalFootprintLimit + 0.01)
    warnings.push("Girilen yapı kitlesi hesaplanan taban oturumu sınırını aşıyor.");
  if (emsalLimit > 0 && totalEmsalArea > emsalLimit + 0.01)
    warnings.push("Toplam emsal alanı imar hakkını aşıyor.");
  if (!shareholders.length) warnings.push("Tapu hissedarları girilmedi.");
  if (shareholders.length && Math.abs(totalSharePercent - 100) > 0.01)
    warnings.push(`Hissedar oranları toplamı %${formatNumber(totalSharePercent)}; %100 olmalı.`);
  if (!units.length) warnings.push("Bağımsız bölümler oluşturulmadı.");
  if (units.length && unassignedUnits.length)
    warnings.push(`${unassignedUnits.length} bağımsız bölüm henüz paylaştırılmadı.`);
  if (invalidSplitUnits.length)
    warnings.push(
      `${invalidSplitUnits.length} ortak tahsisli bağımsız bölümün paylaşım toplamı %100 değil.`
    );
  if (!toNumber(costs.construction_cost_per_m2))
    warnings.push("İnşaat birim maliyeti girilmedi.");
  if (!averagePrice && !units.some((unit) => toNumber(unit.manual_value) > 0))
    warnings.push("Satış değeri veya ortalama m² satış fiyatı girilmedi.");

  const completionChecks = [
    Boolean(project.project_name?.trim()),
    titleArea > 0,
    Boolean(asRatio(zoning.taks) || toNumber(zoning.manual_footprint_area)),
    Boolean(toNumber(zoning.kaks) || toNumber(zoning.manual_emsal_area)),
    municipalFloorCount > 0,
    plannedFloorCount > 0,
    shareholders.length > 0 && Math.abs(totalSharePercent - 100) <= 0.01,
    units.length > 0,
    units.length > 0 && unassignedUnits.length === 0 && invalidSplitUnits.length === 0,
    toNumber(costs.construction_cost_per_m2) > 0,
    totalProjectValue > 0,
  ];
  const completionRate =
    (completionChecks.filter(Boolean).length / completionChecks.length) * 100;

  return {
    titleArea,
    deductionArea,
    netParcelArea,
    usableWidth,
    usableDepth,
    setbackFootprint,
    taksFootprint,
    blockFootprint,
    legalFootprintLimit,
    projectedFootprint,
    emsalLimit,
    municipalFloorCount,
    plannedFloorCount,
    floorClosedArea,
    floorEmsalArea,
    floorCommonArea,
    floorExemptArea,
    totalClosedArea,
    totalEmsalArea,
    unitGrossArea,
    unitNetArea,
    distributableArea,
    totalProjectValue,
    contractorRate,
    ownersRate,
    contractorAreaEntitlement,
    ownersAreaEntitlement,
    contractorValueEntitlement,
    ownersValueEntitlement,
    totalSharePercent,
    contractorAssignedUnits,
    invalidSplitUnits,
    unassignedUnits,
    contractorActualArea,
    contractorActualValue,
    partyResults,
    directConstructionCost,
    fixedCosts,
    contingencyCost,
    totalCost,
    contractorRevenue,
    estimatedProfit,
    profitMargin,
    returnOnCost,
    breakEvenContractorRate,
    warnings,
    completionRate,
  };
}

function SectionCard({ icon: Icon, title, subtitle, action, children, sx = {} }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${COLORS.border}`,
        overflow: "hidden",
        background: COLORS.surface,
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {Icon && (
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  color: COLORS.blue,
                  background: COLORS.blueLight,
                  flexShrink: 0,
                }}
              >
                <Icon fontSize="small" />
              </Box>
            )}
            <Box>
              <Typography fontWeight={950} color={COLORS.text}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color={COLORS.muted}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldGrid({ children, min = 220, sx = {} }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
        gap: 1.5,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function NumberField({ label, value, onChange, suffix, helperText, min, max, ...props }) {
  return (
    <TextField
      fullWidth
      type="number"
      label={label}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      helperText={helperText}
      inputProps={{ min, max, step: "any" }}
      InputProps={
        suffix
          ? {
              endAdornment: <InputAdornment position="end">{suffix}</InputAdornment>,
            }
          : undefined
      }
      {...props}
    />
  );
}

function MetricCard({ label, value, icon: Icon, color = COLORS.blue, light = COLORS.blueLight, hint }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${COLORS.border}`,
        minHeight: 118,
        background: "linear-gradient(145deg, #FFFFFF 0%, #FBFDFF 100%)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography variant="body2" color={COLORS.muted} fontWeight={800}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={950} color={COLORS.text} mt={0.7}>
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" color={COLORS.muted} display="block" mt={0.5}>
              {hint}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.2,
              display: "grid",
              placeItems: "center",
              color,
              background: light,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function EmptyState({ title, description, action }) {
  return (
    <Box sx={{ py: 5, px: 2, textAlign: "center" }}>
      <Box
        sx={{
          width: 68,
          height: 68,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          background: COLORS.blueLight,
          color: COLORS.blue,
          mx: "auto",
          mb: 2,
        }}
      >
        <HomeWorkRoundedIcon fontSize="large" />
      </Box>
      <Typography variant="h6" fontWeight={950} color={COLORS.text}>
        {title}
      </Typography>
      <Typography color={COLORS.muted} mt={0.75} maxWidth={560} mx="auto">
        {description}
      </Typography>
      {action && <Box mt={2.5}>{action}</Box>}
    </Box>
  );
}

function DifferenceChip({ value, mode = "area", currency = "TRY" }) {
  const number = toNumber(value);
  const neutral = Math.abs(number) < 0.01;
  const positive = number > 0;
  const label =
    mode === "money"
      ? `${positive ? "+" : ""}${formatMoney(number, currency)}`
      : `${positive ? "+" : ""}${formatArea(number)}`;

  return (
    <Chip
      size="small"
      label={neutral ? "Dengeli" : label}
      sx={{
        fontWeight: 900,
        color: neutral ? COLORS.green : positive ? COLORS.red : COLORS.amber,
        background: neutral ? COLORS.greenLight : positive ? COLORS.redLight : COLORS.amberLight,
      }}
    />
  );
}

function DataRow({ label, value, strong = false, color }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} py={0.75}>
      <Typography variant="body2" color={COLORS.muted}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={strong ? 950 : 800} color={color || COLORS.text} textAlign="right">
        {value}
      </Typography>
    </Stack>
  );
}

function ProjectStatusChip({ status }) {
  const map = {
    draft: { color: COLORS.muted, background: "#EEF2F7" },
    analysis: { color: COLORS.blue, background: COLORS.blueLight },
    offer: { color: COLORS.orangeDark, background: COLORS.orangeLight },
    agreement: { color: COLORS.purple, background: COLORS.purpleLight },
    approved: { color: COLORS.green, background: COLORS.greenLight },
    archived: { color: COLORS.muted, background: "#EEF2F7" },
  };
  const style = map[status] || map.draft;
  return <Chip size="small" label={statusLabel(status)} sx={{ fontWeight: 900, ...style }} />;
}

function partyMeta(project, partyId) {
  if (partyId === "split") {
    return {
      label: "Ortak paylaşım",
      main: COLORS.purple,
      light: COLORS.purpleLight,
    };
  }
  if (partyId === "contractor") {
    return {
      label: project.agreement?.contractor_name || project.contractor_company || "Müteahhit",
      main: COLORS.orange,
      light: COLORS.orangeLight,
    };
  }

  const index = (project.shareholders || []).findIndex(
    (shareholder) => String(shareholder.client_id) === String(partyId)
  );
  if (index >= 0) {
    const shareholder = project.shareholders[index];
    const color = PARTY_COLORS[toNumber(shareholder.color_index) % PARTY_COLORS.length];
    return {
      label: shareholder.full_name || `${index + 1}. Hissedar`,
      main: color.main,
      light: color.light,
    };
  }

  return { label: "Paylaştırılmadı", main: COLORS.muted, light: "#EEF2F7" };
}

function BuildingPreview({ project, compact = false }) {
  const floors = [...(project.floors || [])].sort(
    (a, b) => toNumber(b.floor_no) - toNumber(a.floor_no)
  );
  const units = project.units || [];

  if (!floors.length && !units.length) {
    return (
      <EmptyState
        title="Bina şeması henüz oluşmadı"
        description="Katları ve bağımsız bölümleri eklediğinizde paylaşım burada kat kat görüntülenecek."
      />
    );
  }

  const virtualFloors = floors.length
    ? floors
    : Array.from(new Set(units.map((unit) => unit.floor_name || "Kat belirtilmedi"))).map(
        (name, index) => ({ client_id: name, name, floor_no: index })
      );

  return (
    <Stack spacing={1.1}>
      {virtualFloors.map((floor) => {
        const floorUnits = units
          .filter(
            (unit) =>
              String(unit.floor_client_id || unit.floor_name) ===
              String(floor.client_id || floor.name)
          )
          .sort((a, b) => String(a.unit_no).localeCompare(String(b.unit_no), "tr", { numeric: true }));

        return (
          <Box
            key={floor.client_id || floor.name}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "72px 1fr", sm: "100px 1fr" },
              borderRadius: 2.5,
              border: `1px solid ${COLORS.border}`,
              overflow: "hidden",
              minHeight: compact ? 60 : 74,
            }}
          >
            <Box
              sx={{
                p: 1,
                background: COLORS.blueDark,
                color: "white",
                display: "grid",
                placeItems: "center",
                textAlign: "center",
              }}
            >
              <Box>
                <Typography fontWeight={950} variant={compact ? "caption" : "body2"}>
                  {floor.name || `${floor.floor_no}. Kat`}
                </Typography>
                {!compact && (
                  <Typography variant="caption" sx={{ opacity: 0.72 }}>
                    {floor.closed_area ? formatArea(floor.closed_area) : ""}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                p: 1,
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(1, minmax(0, 1fr))",
                  sm: `repeat(${Math.min(Math.max(floorUnits.length, 1), 4)}, minmax(0, 1fr))`,
                },
                gap: 1,
                background: "#F8FAFC",
              }}
            >
              {floorUnits.length ? (
                floorUnits.map((unit) => {
                  const party = partyMeta(project, unit.allocation_party);
                  return (
                    <Box
                      key={unit.client_id}
                      sx={{
                        p: compact ? 0.8 : 1.1,
                        borderRadius: 2,
                        border: `1px solid ${party.main}35`,
                        borderLeft: `5px solid ${party.main}`,
                        background: party.light,
                        minWidth: 0,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography fontWeight={950} noWrap variant={compact ? "caption" : "body2"}>
                          No {unit.unit_no || "-"}
                        </Typography>
                        {!compact && (
                          <Typography variant="caption" fontWeight={800} color={COLORS.muted} noWrap>
                            {unit.unit_type}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="caption" display="block" color={COLORS.muted} noWrap>
                        {formatArea(unit.gross_area)}
                      </Typography>
                      <Typography variant="caption" display="block" fontWeight={900} color={party.main} noWrap>
                        {party.label}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color={COLORS.muted} sx={{ p: 1 }}>
                  Bu kata bağımsız bölüm eklenmedi.
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function AllocationProgress({ label, actual, target, mode = "area", currency = "TRY", color }) {
  const safeTarget = toNumber(target);
  const safeActual = toNumber(actual);
  const rate = safeTarget > 0 ? clamp((safeActual / safeTarget) * 100, 0, 150) : 0;
  const display = mode === "money" ? formatMoney : formatArea;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={0.7}>
        <Typography variant="body2" fontWeight={900} color={COLORS.text} noWrap>
          {label}
        </Typography>
        <Typography variant="caption" color={COLORS.muted} textAlign="right">
          {display(safeActual, currency)} / {display(safeTarget, currency)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(rate, 100)}
        sx={{
          height: 9,
          borderRadius: 99,
          background: "#E8EDF4",
          "& .MuiLinearProgress-bar": {
            borderRadius: 99,
            background: rate > 105 ? COLORS.red : color || COLORS.blue,
          },
        }}
      />
    </Box>
  );
}

function automaticAllocation(project, calculation) {
  const units = [...(project.units || [])];
  const basis = project.agreement?.allocation_basis || "value";
  const fallbackPrice = project.costs?.average_sales_price_per_m2;
  const totalMetric =
    basis === "unit"
      ? units.length
      : basis === "area"
        ? calculation.distributableArea
        : calculation.totalProjectValue || calculation.distributableArea;

  if (!units.length || totalMetric <= 0) return units;

  const parties = [
    {
      id: "contractor",
      target: totalMetric * (calculation.contractorRate / 100),
      actual: 0,
    },
    ...(project.shareholders || []).map((shareholder) => ({
      id: String(shareholder.client_id),
      target:
        totalMetric *
        (calculation.ownersRate / 100) *
        (shareholderPercent(shareholder) / 100),
      actual: 0,
    })),
  ].filter((party) => party.target > 0);

  const metricOf = (unit) =>
    basis === "unit"
      ? 1
      : basis === "area"
        ? toNumber(unit.gross_area)
        : unitValue(unit, fallbackPrice) || toNumber(unit.gross_area);

  const sorted = units
    .map((unit, index) => ({ unit, index, metric: metricOf(unit) }))
    .sort((a, b) => b.metric - a.metric);

  const assignments = new Map();
  sorted.forEach(({ unit, metric }) => {
    const selected = [...parties].sort((a, b) => {
      const aDelta =
        Math.abs(a.actual + metric - a.target) - Math.abs(a.actual - a.target);
      const bDelta =
        Math.abs(b.actual + metric - b.target) - Math.abs(b.actual - b.target);
      if (Math.abs(aDelta - bDelta) > 0.000001) return aDelta - bDelta;
      return b.target - b.actual - (a.target - a.actual);
    })[0];

    if (selected) {
      assignments.set(unit.client_id, selected.id);
      selected.actual += metric;
    }
  });

  return units.map((unit) => ({
    ...unit,
    allocation_party: assignments.get(unit.client_id) || "unassigned",
    allocation_splits: [],
  }));
}

export default function PanelArsaPayi() {
  const mobile = useMediaQuery("(max-width:900px)");
  const verySmall = useMediaQuery("(max-width:520px)");

  const [activeTab, setActiveTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(emptyProject());
  const [editorOpen, setEditorOpen] = useState(false);
  const [projectsDialog, setProjectsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [allocationDialog, setAllocationDialog] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingProject, setLoadingProject] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverOnline, setServerOnline] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const calculation = useMemo(() => calculateProject(project), [project]);
  const currency = project.agreement?.currency || "TRY";

  const notify = useCallback((message, severity = "success") => {
    setNotification({ open: true, message, severity });
  }, []);

  const apiRequest = useCallback(async (path = "", options = {}) => {
    const response = await fetch(`${YAPIPAY_API}${path}`, {
      ...options,
      headers: {
        ...tokenHeaders(),
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      const error = new Error(
        data.message || data.error || `Sunucu işlemi başarısız (${response.status})`
      );
      error.status = response.status;
      throw error;
    }

    return data;
  }, []);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await apiRequest("/projects");
      const rows = response.projects || response.data || [];
      setProjects(Array.isArray(rows) ? rows : []);
      setServerOnline(true);
    } catch (error) {
      setProjects([]);
      setServerOnline(false);
      notify(
        error.status === 404
          ? "YapıPay backend servisi henüz sunucuda bulunamadı. Frontend kullanıma hazır."
          : error.message,
        "warning"
      );
    } finally {
      setLoadingProjects(false);
    }
  }, [apiRequest, notify]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    const warnUnsaved = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnUnsaved);
    return () => window.removeEventListener("beforeunload", warnUnsaved);
  }, [dirty]);

  const markProject = useCallback((updater) => {
    setProject((previous) =>
      typeof updater === "function" ? updater(previous) : updater
    );
    setDirty(true);
  }, []);

  const updateRoot = useCallback(
    (key, value) => markProject((previous) => ({ ...previous, [key]: value })),
    [markProject]
  );

  const updateSection = useCallback(
    (section, key, value) =>
      markProject((previous) => ({
        ...previous,
        [section]: {
          ...(previous[section] || {}),
          [key]: value,
        },
      })),
    [markProject]
  );

  const updateRow = useCallback(
    (collection, clientId, key, value) =>
      markProject((previous) => ({
        ...previous,
        [collection]: (previous[collection] || []).map((row) =>
          String(row.client_id) === String(clientId) ? { ...row, [key]: value } : row
        ),
      })),
    [markProject]
  );

  const updateUnitSplit = useCallback(
    (unitClientId, partyId, percent) =>
      markProject((previous) => ({
        ...previous,
        units: (previous.units || []).map((unit) => {
          if (String(unit.client_id) !== String(unitClientId)) return unit;
          const others = (unit.allocation_splits || []).filter(
            (split) => String(split.party_id) !== String(partyId)
          );
          return {
            ...unit,
            allocation_splits:
              percent === "" || toNumber(percent) === 0
                ? others
                : [...others, { party_id: String(partyId), percent }],
          };
        }),
      })),
    [markProject]
  );

  const addRow = useCallback(
    (collection, row) =>
      markProject((previous) => ({
        ...previous,
        [collection]: [...(previous[collection] || []), row],
      })),
    [markProject]
  );

  const removeRow = useCallback(
    (collection, clientId) =>
      markProject((previous) => ({
        ...previous,
        [collection]: (previous[collection] || []).filter(
          (row) => String(row.client_id) !== String(clientId)
        ),
        ...(collection === "shareholders"
          ? {
              units: (previous.units || []).map((unit) =>
                String(unit.allocation_party) === String(clientId)
                  ? { ...unit, allocation_party: "unassigned" }
                  : unit
              ),
            }
          : {}),
        ...(collection === "floors"
          ? {
              units: (previous.units || []).map((unit) =>
                String(unit.floor_client_id) === String(clientId)
                  ? { ...unit, floor_client_id: "", floor_name: "" }
                  : unit
              ),
            }
          : {}),
      })),
    [markProject]
  );

  const createNewProject = () => {
    if (dirty && !window.confirm("Kaydedilmemiş değişiklikler var. Yeni projeye geçilsin mi?")) {
      return;
    }
    setProject(emptyProject());
    setEditorOpen(true);
    setProjectsDialog(false);
    setActiveTab(0);
    setDirty(false);
  };

  const openProject = async (projectId) => {
    if (!projectId) return;
    if (dirty && !window.confirm("Kaydedilmemiş değişiklikler var. Başka proje açılsın mı?")) {
      return;
    }

    setLoadingProject(true);
    try {
      const response = await apiRequest(`/projects/${encodeURIComponent(projectId)}`);
      setProject(hydrateProject(response.project || response.data || response));
      setEditorOpen(true);
      setProjectsDialog(false);
      setActiveTab(0);
      setDirty(false);
      setServerOnline(true);
    } catch (error) {
      setServerOnline(false);
      notify(error.message, "error");
    } finally {
      setLoadingProject(false);
    }
  };

  const saveProject = async () => {
    if (!project.project_name?.trim()) {
      notify("Kaydetmeden önce proje adını girin.", "warning");
      setActiveTab(1);
      return;
    }

    setSaving(true);
    try {
      const isUpdate = Boolean(project.id);
      const response = await apiRequest(
        isUpdate ? `/projects/${encodeURIComponent(project.id)}` : "/projects",
        {
          method: isUpdate ? "PUT" : "POST",
          body: JSON.stringify({
            project,
            calculation_snapshot: calculation,
          }),
        }
      );

      const returned = response.project || response.data || response;
      const saved = hydrateProject({
        ...project,
        ...(returned && typeof returned === "object" ? returned : {}),
        id:
          returned?.id ||
          returned?.project_id ||
          project.id,
      });
      setProject(saved);
      setDirty(false);
      setServerOnline(true);
      notify(isUpdate ? "Proje güncellendi." : "Proje sunucuya kaydedildi.");
      await loadProjects();
    } catch (error) {
      setServerOnline(false);
      notify(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async () => {
    if (!project.id) {
      setProject(emptyProject());
      setEditorOpen(false);
      setDeleteDialog(false);
      setDirty(false);
      return;
    }

    setSaving(true);
    try {
      await apiRequest(`/projects/${encodeURIComponent(project.id)}`, {
        method: "DELETE",
      });
      setProject(emptyProject());
      setEditorOpen(false);
      setDeleteDialog(false);
      setDirty(false);
      setServerOnline(true);
      notify("Proje silindi.");
      await loadProjects();
    } catch (error) {
      setServerOnline(false);
      notify(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const generateStructure = () => {
    const floorCount = Math.floor(toNumber(project.structure.planned_floor_count));
    const unitsPerFloor = Math.floor(toNumber(project.structure.units_per_floor));
    const floorArea = toNumber(project.structure.typical_floor_closed_area);

    if (floorCount <= 0 || unitsPerFloor <= 0 || floorArea <= 0) {
      notify("Kat adedi, kattaki daire sayısı ve tipik kat kapalı alanını girin.", "warning");
      setGenerateDialog(false);
      return;
    }

    const commonRate = asRatio(project.structure.common_area_rate);
    const netGrossRate = asRatio(project.structure.net_gross_rate) || 0.8;
    const commonPerFloor = floorArea * commonRate;
    const distributablePerFloor = Math.max(0, floorArea - commonPerFloor);
    const grossPerUnit = distributablePerFloor / unitsPerFloor;
    const netPerUnit = grossPerUnit * netGrossRate;
    const floors = [];
    const units = [];
    let unitNo = 1;

    for (let index = 0; index < floorCount; index += 1) {
      const floor = emptyFloor(index);
      floor.name = index === 0 ? "Zemin Kat" : `${index}. Kat`;
      floor.floor_no = index;
      floor.floor_type = index === 0 ? "ground" : "normal";
      floor.closed_area = round(floorArea);
      floor.emsal_area = round(floorArea);
      floor.common_area = round(commonPerFloor);
      floor.unit_count = unitsPerFloor;
      floors.push(floor);

      for (let unitIndex = 0; unitIndex < unitsPerFloor; unitIndex += 1) {
        const unit = emptyUnit(unitNo);
        unit.floor_client_id = floor.client_id;
        unit.floor_name = floor.name;
        unit.gross_area = round(grossPerUnit);
        unit.net_area = round(netPerUnit);
        units.push(unit);
        unitNo += 1;
      }
    }

    markProject((previous) => ({ ...previous, floors, units }));
    setGenerateDialog(false);
    notify(`${floors.length} kat ve ${units.length} bağımsız bölüm oluşturuldu.`);
  };

  const distributeEqually = () => {
    const count = project.shareholders.length;
    if (!count) {
      notify("Önce hissedarları ekleyin.", "warning");
      return;
    }

    markProject((previous) => ({
      ...previous,
      shareholders: previous.shareholders.map((shareholder) => ({
        ...shareholder,
        share_numerator: 1,
        share_denominator: count,
        manual_percent: "",
      })),
    }));
    notify(`Hisseler ${count} kişi arasında eşit dağıtıldı.`);
  };

  const applyAutomaticAllocation = () => {
    if (!project.units.length) {
      notify("Önce bağımsız bölümleri oluşturun.", "warning");
      setAllocationDialog(false);
      return;
    }
    if (!project.shareholders.length || Math.abs(calculation.totalSharePercent - 100) > 0.01) {
      notify("Otomatik paylaşım için hissedar oranları toplamı %100 olmalı.", "warning");
      setAllocationDialog(false);
      return;
    }

    const units = automaticAllocation(project, calculation);
    markProject((previous) => ({ ...previous, units }));
    setAllocationDialog(false);
    notify("Bağımsız bölümler hak değerlerine en yakın biçimde paylaştırıldı.");
  };

  const printReport = () => {
    setActiveTab(7);
    window.setTimeout(() => window.print(), 180);
  };

  const filteredProjects = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase("tr-TR");
    if (!query) return projects;
    return projects.filter((item) =>
      [
        item.project_name,
        item.project_no,
        item.customer_name,
        item.city,
        item.district,
        item.parcel?.city,
        item.parcel?.district,
        item.parcel?.block_no,
        item.parcel?.parcel_no,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr-TR")
        .includes(query)
    );
  }, [projects, searchText]);

  const goPrevious = () => setActiveTab((value) => Math.max(0, value - 1));
  const goNext = () => setActiveTab((value) => Math.min(TABS.length - 1, value + 1));

  const renderOverview = () => (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <MetricCard
          label="Net Parsel Alanı"
          value={formatArea(calculation.netParcelArea)}
          icon={SquareFootRoundedIcon}
          hint={`Tapu: ${formatArea(calculation.titleArea)}`}
        />
        <MetricCard
          label="Toplam Kapalı Alan"
          value={formatArea(calculation.totalClosedArea)}
          icon={ApartmentRoundedIcon}
          color={COLORS.purple}
          light={COLORS.purpleLight}
          hint={`${calculation.plannedFloorCount || 0} planlanan kat`}
        />
        <MetricCard
          label="Bağımsız Bölüm"
          value={`${project.units.length} adet`}
          icon={BusinessRoundedIcon}
          color={COLORS.cyan}
          light={COLORS.cyanLight}
          hint={`${calculation.unassignedUnits.length} paylaştırılmamış`}
        />
        <MetricCard
          label="Tahmini Proje Değeri"
          value={formatMoney(calculation.totalProjectValue, currency)}
          icon={AssessmentRoundedIcon}
          color={COLORS.green}
          light={COLORS.greenLight}
          hint={`Müteahhit payı ${formatPercent(calculation.contractorRate)}`}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.4fr) minmax(320px, .6fr)" },
          gap: 2,
        }}
      >
        <SectionCard
          icon={ApartmentRoundedIcon}
          title="Proje Paylaşım Özeti"
          subtitle="Girilen veriler değiştikçe sonuçlar anında güncellenir."
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 3, background: COLORS.orangeLight, border: `1px solid ${COLORS.orange}25` }}
            >
              <Typography variant="body2" color={COLORS.orangeDark} fontWeight={950}>
                {project.agreement.contractor_name || project.contractor_company || "Müteahhit"}
              </Typography>
              <Typography variant="h5" fontWeight={950} mt={0.5}>
                {formatPercent(calculation.contractorRate)}
              </Typography>
              <Typography variant="body2" color={COLORS.muted} mt={0.5}>
                {formatArea(calculation.contractorAreaEntitlement)} ·{" "}
                {formatMoney(calculation.contractorValueEntitlement, currency)}
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 3, background: COLORS.blueLight, border: `1px solid ${COLORS.blue}25` }}
            >
              <Typography variant="body2" color={COLORS.blue} fontWeight={950}>
                Arsa Sahipleri
              </Typography>
              <Typography variant="h5" fontWeight={950} mt={0.5}>
                {formatPercent(calculation.ownersRate)}
              </Typography>
              <Typography variant="body2" color={COLORS.muted} mt={0.5}>
                {formatArea(calculation.ownersAreaEntitlement)} ·{" "}
                {formatMoney(calculation.ownersValueEntitlement, currency)}
              </Typography>
            </Paper>
          </Box>
          <BuildingPreview project={project} compact />
        </SectionCard>

        <Stack spacing={2}>
          <SectionCard
            icon={CheckCircleRoundedIcon}
            title="Proje Hazırlık Durumu"
            subtitle="Eksiksiz rapor için gerekli veri kontrolü"
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1}>
              <Typography variant="h4" fontWeight={950} color={COLORS.blue}>
                {formatPercent(calculation.completionRate)}
              </Typography>
              <Typography variant="caption" color={COLORS.muted}>
                tamamlandı
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={calculation.completionRate}
              sx={{
                height: 11,
                borderRadius: 99,
                background: "#E7EDF5",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  background: `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.orange})`,
                },
              }}
            />
          </SectionCard>

          <SectionCard
            icon={calculation.warnings.length ? WarningAmberRoundedIcon : CheckCircleRoundedIcon}
            title={calculation.warnings.length ? "Kontrol Edilecekler" : "Tüm Kontroller Tamam"}
            subtitle={
              calculation.warnings.length
                ? `${calculation.warnings.length} veri veya hesap kontrolü gerekiyor.`
                : "Proje verileri raporlamaya hazır."
            }
          >
            <Stack spacing={1}>
              {calculation.warnings.slice(0, 7).map((warning, index) => (
                <Stack key={`${warning}_${index}`} direction="row" spacing={1} alignItems="flex-start">
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: COLORS.amber, mt: "2px" }} />
                  <Typography variant="body2" color={COLORS.text}>
                    {warning}
                  </Typography>
                </Stack>
              ))}
              {!calculation.warnings.length && (
                <Alert severity="success" sx={{ borderRadius: 2.5 }}>
                  İmar, hisse, paylaşım ve fizibilite kontrolleri tamamlandı.
                </Alert>
              )}
              {calculation.warnings.length > 7 && (
                <Button size="small" onClick={() => setActiveTab(7)}>
                  Tümünü raporda görüntüle
                </Button>
              )}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </Stack>
  );

  const renderParcel = () => (
    <Stack spacing={2}>
      <SectionCard
        icon={DescriptionRoundedIcon}
        title="Proje Kimliği"
        subtitle="Raporlarda ve proje arşivinde kullanılacak temel bilgiler"
      >
        <FieldGrid>
          <TextField
            fullWidth
            required
            label="Proje Adı"
            value={project.project_name}
            onChange={(event) => updateRoot("project_name", event.target.value)}
          />
          <TextField
            fullWidth
            label="Proje / Dosya No"
            value={project.project_no}
            onChange={(event) => updateRoot("project_no", event.target.value)}
          />
          <TextField
            select
            fullWidth
            label="Proje Durumu"
            value={project.status}
            onChange={(event) => updateRoot("status", event.target.value)}
          >
            {PROJECT_STATUSES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="date"
            label="Rapor Tarihi"
            value={project.report_date || ""}
            onChange={(event) => updateRoot("report_date", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Müşteri / Arsa Sahibi Grubu"
            value={project.customer_name}
            onChange={(event) => updateRoot("customer_name", event.target.value)}
          />
          <TextField
            fullWidth
            label="Mimar / Proje Sorumlusu"
            value={project.architect_name}
            onChange={(event) => updateRoot("architect_name", event.target.value)}
          />
          <TextField
            fullWidth
            label="Müteahhit Firma"
            value={project.contractor_company}
            onChange={(event) => {
              updateRoot("contractor_company", event.target.value);
              if (!project.agreement.contractor_name) {
                updateSection("agreement", "contractor_name", event.target.value);
              }
            }}
          />
        </FieldGrid>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Proje Açıklaması"
          value={project.description}
          onChange={(event) => updateRoot("description", event.target.value)}
          sx={{ mt: 1.5 }}
        />
      </SectionCard>

      <SectionCard
        icon={HomeWorkRoundedIcon}
        title="Tapu ve Parsel Bilgileri"
        subtitle="Resmî tapu belgesindeki alan, ada, parsel ve adres bilgileri"
      >
        <FieldGrid>
          <TextField
            fullWidth
            label="İl"
            value={project.parcel.city}
            onChange={(event) => updateSection("parcel", "city", event.target.value)}
          />
          <TextField
            fullWidth
            label="İlçe"
            value={project.parcel.district}
            onChange={(event) => updateSection("parcel", "district", event.target.value)}
          />
          <TextField
            fullWidth
            label="Mahalle"
            value={project.parcel.neighborhood}
            onChange={(event) => updateSection("parcel", "neighborhood", event.target.value)}
          />
          <TextField
            fullWidth
            label="Pafta No"
            value={project.parcel.sheet_no}
            onChange={(event) => updateSection("parcel", "sheet_no", event.target.value)}
          />
          <TextField
            fullWidth
            label="Ada No"
            value={project.parcel.block_no}
            onChange={(event) => updateSection("parcel", "block_no", event.target.value)}
          />
          <TextField
            fullWidth
            label="Parsel No"
            value={project.parcel.parcel_no}
            onChange={(event) => updateSection("parcel", "parcel_no", event.target.value)}
          />
          <NumberField
            label="Tapu Yüzölçümü"
            value={project.parcel.title_area}
            onChange={(value) => updateSection("parcel", "title_area", value)}
            suffix="m²"
            min={0}
          />
          <NumberField
            label="Yol / Kamu Terk Alanı"
            value={project.parcel.deduction_area}
            onChange={(value) => updateSection("parcel", "deduction_area", value)}
            suffix="m²"
            min={0}
          />
          <TextField
            fullWidth
            label="Tapu Niteliği"
            value={project.parcel.title_type}
            onChange={(event) => updateSection("parcel", "title_type", event.target.value)}
            placeholder="Arsa, kargir apartman, tarla..."
          />
          <NumberField
            label="Mevcut Bina Kapalı Alanı"
            value={project.parcel.existing_building_area}
            onChange={(value) => updateSection("parcel", "existing_building_area", value)}
            suffix="m²"
            min={0}
          />
          <NumberField
            label="Mevcut Bağımsız Bölüm"
            value={project.parcel.existing_unit_count}
            onChange={(value) => updateSection("parcel", "existing_unit_count", value)}
            suffix="adet"
            min={0}
          />
        </FieldGrid>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Açık Adres"
          value={project.parcel.address}
          onChange={(event) => updateSection("parcel", "address", event.target.value)}
          sx={{ mt: 1.5 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Tapu Notları"
          value={project.parcel.title_notes}
          onChange={(event) => updateSection("parcel", "title_notes", event.target.value)}
          sx={{ mt: 1.5 }}
        />
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 3,
            background: COLORS.blueLight,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1.5,
          }}
        >
          <DataRow label="Tapu alanı" value={formatArea(calculation.titleArea)} strong />
          <DataRow label="Terk/kayıp" value={formatArea(calculation.deductionArea)} strong />
          <DataRow label="Net parsel" value={formatArea(calculation.netParcelArea)} strong color={COLORS.blue} />
        </Box>
      </SectionCard>
    </Stack>
  );

  const renderZoning = () => (
    <Stack spacing={2}>
      <SectionCard
        icon={ArchitectureRoundedIcon}
        title="Resmî İmar Bilgileri"
        subtitle="İmar durumu belgesi ve belediye plan notlarındaki değerleri girin."
      >
        <FieldGrid>
          <TextField
            fullWidth
            type="date"
            label="İmar Belgesi Tarihi"
            value={project.zoning.zoning_document_date || ""}
            onChange={(event) => updateSection("zoning", "zoning_document_date", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="İmar Belgesi No"
            value={project.zoning.zoning_document_no}
            onChange={(event) => updateSection("zoning", "zoning_document_no", event.target.value)}
          />
          <TextField
            fullWidth
            label="Plan Ölçeği"
            value={project.zoning.plan_scale}
            onChange={(event) => updateSection("zoning", "plan_scale", event.target.value)}
            placeholder="1/1000"
          />
          <TextField
            select
            fullWidth
            label="Yapı Nizamı"
            value={project.zoning.building_order}
            onChange={(event) => updateSection("zoning", "building_order", event.target.value)}
          >
            {BUILDING_ORDERS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <NumberField
            label="TAKS"
            value={project.zoning.taks}
            onChange={(value) => updateSection("zoning", "taks", value)}
            helperText="0,40 veya 40 şeklinde girilebilir"
            min={0}
          />
          <NumberField
            label="KAKS / Emsal"
            value={project.zoning.kaks}
            onChange={(value) => updateSection("zoning", "kaks", value)}
            helperText="Örn. 2,10"
            min={0}
          />
          <NumberField
            label="Belediye Kat Adedi"
            value={project.zoning.municipal_floor_count}
            onChange={(value) => updateSection("zoning", "municipal_floor_count", value)}
            suffix="kat"
            min={0}
          />
          <NumberField
            label="Hmax / Bina Yüksekliği"
            value={project.zoning.hmax}
            onChange={(value) => updateSection("zoning", "hmax", value)}
            suffix="m"
            min={0}
          />
          <TextField
            select
            fullWidth
            label="Veri Doğrulama Durumu"
            value={project.zoning.verification_status}
            onChange={(event) => updateSection("zoning", "verification_status", event.target.value)}
          >
            <MenuItem value="estimated">Tahmini</MenuItem>
            <MenuItem value="documented">Belgeye dayalı</MenuItem>
            <MenuItem value="architect_approved">Mimar onaylı</MenuItem>
          </TextField>
          <NumberField
            label="Elle Girilen Azami Emsal Alanı"
            value={project.zoning.manual_emsal_area}
            onChange={(value) => updateSection("zoning", "manual_emsal_area", value)}
            suffix="m²"
            helperText="Belediye özel bir toplam alan verdiyse"
            min={0}
          />
        </FieldGrid>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Belediye Plan Notları"
          value={project.zoning.plan_notes}
          onChange={(event) => updateSection("zoning", "plan_notes", event.target.value)}
          sx={{ mt: 1.5 }}
        />
      </SectionCard>

      <SectionCard
        icon={SquareFootRoundedIcon}
        title="Parsel Geometrisi ve Çekme Mesafeleri"
        subtitle="Yapı yaklaşma sınırlarından kullanılabilir kitle alanını hesaplar."
      >
        <FieldGrid>
          <NumberField
            label="Parsel Genişliği"
            value={project.zoning.parcel_width}
            onChange={(value) => updateSection("zoning", "parcel_width", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Parsel Derinliği"
            value={project.zoning.parcel_depth}
            onChange={(value) => updateSection("zoning", "parcel_depth", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Ön Bahçe Çekmesi"
            value={project.zoning.front_setback}
            onChange={(value) => updateSection("zoning", "front_setback", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Arka Bahçe Çekmesi"
            value={project.zoning.rear_setback}
            onChange={(value) => updateSection("zoning", "rear_setback", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Sol Yan Bahçe"
            value={project.zoning.left_setback}
            onChange={(value) => updateSection("zoning", "left_setback", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Sağ Yan Bahçe"
            value={project.zoning.right_setback}
            onChange={(value) => updateSection("zoning", "right_setback", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Belediye Kitle Genişliği"
            value={project.zoning.block_width}
            onChange={(value) => updateSection("zoning", "block_width", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Belediye Kitle Derinliği"
            value={project.zoning.block_depth}
            onChange={(value) => updateSection("zoning", "block_depth", value)}
            suffix="m"
            min={0}
          />
          <NumberField
            label="Elle Girilen Taban Oturumu"
            value={project.zoning.manual_footprint_area}
            onChange={(value) => updateSection("zoning", "manual_footprint_area", value)}
            suffix="m²"
            min={0}
          />
          <NumberField
            label="Yol Kot Farkı"
            value={project.zoning.road_level_difference}
            onChange={(value) => updateSection("zoning", "road_level_difference", value)}
            suffix="m"
          />
        </FieldGrid>
      </SectionCard>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" },
          gap: 1.5,
        }}
      >
        <MetricCard label="TAKS Taban Sınırı" value={formatArea(calculation.taksFootprint)} icon={SquareFootRoundedIcon} />
        <MetricCard
          label="Çekmeler Sonrası Kitle"
          value={formatArea(calculation.setbackFootprint)}
          icon={ArchitectureRoundedIcon}
          color={COLORS.purple}
          light={COLORS.purpleLight}
        />
        <MetricCard
          label="Kullanılan Taban Oturumu"
          value={formatArea(calculation.projectedFootprint)}
          icon={BusinessRoundedIcon}
          color={COLORS.orange}
          light={COLORS.orangeLight}
        />
        <MetricCard
          label="Azami Emsal Alanı"
          value={formatArea(calculation.emsalLimit)}
          icon={AssessmentRoundedIcon}
          color={COLORS.green}
          light={COLORS.greenLight}
        />
      </Box>
    </Stack>
  );

  const renderBuilding = () => (
    <Stack spacing={2}>
      <SectionCard
        icon={AutoAwesomeRoundedIcon}
        title="Akıllı Kat ve Daire Oluşturucu"
        subtitle="Gerçek proje değerlerini girin; sistem katları ve bağımsız bölümleri otomatik oluştursun."
        action={
          <Button
            variant="contained"
            startIcon={<AutoAwesomeRoundedIcon />}
            onClick={() =>
              project.floors.length || project.units.length
                ? setGenerateDialog(true)
                : generateStructure()
            }
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 900,
              background: COLORS.orange,
              "&:hover": { background: COLORS.orangeDark },
            }}
          >
            Kat ve Daireleri Oluştur
          </Button>
        }
      >
        <FieldGrid>
          <NumberField
            label="Planlanan Toplam Kat"
            value={project.structure.planned_floor_count}
            onChange={(value) => updateSection("structure", "planned_floor_count", value)}
            suffix="kat"
            min={1}
          />
          <NumberField
            label="Her Kattaki Daire"
            value={project.structure.units_per_floor}
            onChange={(value) => updateSection("structure", "units_per_floor", value)}
            suffix="adet"
            min={1}
          />
          <NumberField
            label="Tipik Kat Kapalı Alanı"
            value={project.structure.typical_floor_closed_area}
            onChange={(value) => updateSection("structure", "typical_floor_closed_area", value)}
            suffix="m²"
            min={0}
          />
          <NumberField
            label="Ortak Alan Oranı"
            value={project.structure.common_area_rate}
            onChange={(value) => updateSection("structure", "common_area_rate", value)}
            suffix="%"
            helperText="Merdiven, asansör ve holler"
            min={0}
            max={100}
          />
          <NumberField
            label="Net / Brüt Oranı"
            value={project.structure.net_gross_rate}
            onChange={(value) => updateSection("structure", "net_gross_rate", value)}
            suffix="%"
            helperText="Örn. 80"
            min={0}
            max={100}
          />
          <NumberField
            label="Dağıtılabilir Alan (İsteğe Bağlı)"
            value={project.structure.distributable_area_override}
            onChange={(value) => updateSection("structure", "distributable_area_override", value)}
            suffix="m²"
            helperText="Girildiğinde bağımsız bölüm toplamının yerine kullanılır"
            min={0}
          />
        </FieldGrid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={950} mb={1.5}>
          Kat türü dağılımı
        </Typography>
        <FieldGrid>
          <NumberField
            label="Bodrum Kat"
            value={project.zoning.basement_count}
            onChange={(value) => updateSection("zoning", "basement_count", value)}
            suffix="adet"
            min={0}
          />
          <NumberField
            label="Zemin Kat"
            value={project.zoning.ground_floor_count}
            onChange={(value) => updateSection("zoning", "ground_floor_count", value)}
            suffix="adet"
            min={0}
          />
          <NumberField
            label="Normal Kat"
            value={project.zoning.normal_floor_count}
            onChange={(value) => updateSection("zoning", "normal_floor_count", value)}
            suffix="adet"
            min={0}
          />
          <NumberField
            label="Çatı Katı"
            value={project.zoning.attic_floor_count}
            onChange={(value) => updateSection("zoning", "attic_floor_count", value)}
            suffix="adet"
            min={0}
          />
        </FieldGrid>
      </SectionCard>

      <SectionCard
        icon={ArchitectureRoundedIcon}
        title="Katlar"
        subtitle={`${project.floors.length} kat kaydı · ${formatArea(calculation.floorClosedArea)} kapalı alan`}
        action={
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={() => addRow("floors", emptyFloor(project.floors.length))}
            sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
          >
            Kat Ekle
          </Button>
        }
      >
        <Stack spacing={1.5}>
          {project.floors.map((floor, index) => (
            <Paper
              key={floor.client_id}
              elevation={0}
              sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, border: `1px solid ${COLORS.border}` }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      background: COLORS.blueLight,
                      color: COLORS.blue,
                      fontWeight: 950,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography fontWeight={950} noWrap>
                    {floor.name || "Adsız kat"}
                  </Typography>
                </Stack>
                <Tooltip title="Katı sil">
                  <IconButton color="error" onClick={() => removeRow("floors", floor.client_id)}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <FieldGrid min={170}>
                <TextField
                  fullWidth
                  label="Kat Adı"
                  value={floor.name}
                  onChange={(event) => updateRow("floors", floor.client_id, "name", event.target.value)}
                />
                <NumberField
                  label="Kat No"
                  value={floor.floor_no}
                  onChange={(value) => updateRow("floors", floor.client_id, "floor_no", value)}
                />
                <TextField
                  select
                  fullWidth
                  label="Kat Türü"
                  value={floor.floor_type}
                  onChange={(event) =>
                    updateRow("floors", floor.client_id, "floor_type", event.target.value)
                  }
                >
                  {FLOOR_TYPES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
                <NumberField
                  label="Kapalı Alan"
                  value={floor.closed_area}
                  onChange={(value) => updateRow("floors", floor.client_id, "closed_area", value)}
                  suffix="m²"
                  min={0}
                />
                <NumberField
                  label="Emsale Dahil Alan"
                  value={floor.emsal_area}
                  onChange={(value) => updateRow("floors", floor.client_id, "emsal_area", value)}
                  suffix="m²"
                  min={0}
                />
                <NumberField
                  label="Ortak Alan"
                  value={floor.common_area}
                  onChange={(value) => updateRow("floors", floor.client_id, "common_area", value)}
                  suffix="m²"
                  min={0}
                />
                <NumberField
                  label="Emsal Harici Alan"
                  value={floor.exempt_area}
                  onChange={(value) => updateRow("floors", floor.client_id, "exempt_area", value)}
                  suffix="m²"
                  min={0}
                />
              </FieldGrid>
            </Paper>
          ))}
          {!project.floors.length && (
            <EmptyState
              title="Kat kaydı bulunmuyor"
              description="Akıllı oluşturucuyu kullanın veya katları tek tek ekleyin."
            />
          )}
        </Stack>
      </SectionCard>

      <SectionCard
        icon={ApartmentRoundedIcon}
        title="Bağımsız Bölümler"
        subtitle={`${project.units.length} bağımsız bölüm · ${formatArea(calculation.unitGrossArea)} brüt · ${formatArea(calculation.unitNetArea)} net`}
        action={
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={() => addRow("units", emptyUnit(project.units.length + 1))}
            sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
          >
            Bağımsız Bölüm Ekle
          </Button>
        }
      >
        <Stack spacing={1.5}>
          {project.units.map((unit, index) => (
            <Paper
              key={unit.client_id}
              elevation={0}
              sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, border: `1px solid ${COLORS.border}` }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      background: COLORS.orangeLight,
                      color: COLORS.orangeDark,
                      fontWeight: 950,
                    }}
                  >
                    {unit.unit_no || index + 1}
                  </Box>
                  <Box minWidth={0}>
                    <Typography fontWeight={950} noWrap>
                      Bağımsız Bölüm {unit.unit_no || index + 1}
                    </Typography>
                    <Typography variant="caption" color={COLORS.muted} noWrap display="block">
                      {unit.floor_name || "Kat seçilmedi"} · {unit.unit_type || "Tür seçilmedi"}
                    </Typography>
                  </Box>
                </Stack>
                <Tooltip title="Bağımsız bölümü sil">
                  <IconButton color="error" onClick={() => removeRow("units", unit.client_id)}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <FieldGrid min={165}>
                <TextField
                  fullWidth
                  label="Bağımsız Bölüm No"
                  value={unit.unit_no}
                  onChange={(event) => updateRow("units", unit.client_id, "unit_no", event.target.value)}
                />
                <TextField
                  select
                  fullWidth
                  label="Kat"
                  value={unit.floor_client_id}
                  onChange={(event) => {
                    const floor = project.floors.find(
                      (item) => String(item.client_id) === String(event.target.value)
                    );
                    updateRow("units", unit.client_id, "floor_client_id", event.target.value);
                    updateRow("units", unit.client_id, "floor_name", floor?.name || "");
                  }}
                >
                  <MenuItem value="">Kat seçilmedi</MenuItem>
                  {project.floors.map((floor) => (
                    <MenuItem key={floor.client_id} value={floor.client_id}>
                      {floor.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Bölüm Türü"
                  value={unit.unit_type}
                  onChange={(event) => updateRow("units", unit.client_id, "unit_type", event.target.value)}
                >
                  {UNIT_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Kullanım"
                  value={unit.usage_type}
                  onChange={(event) => updateRow("units", unit.client_id, "usage_type", event.target.value)}
                >
                  <MenuItem value="residential">Konut</MenuItem>
                  <MenuItem value="commercial">Ticari</MenuItem>
                  <MenuItem value="office">Ofis</MenuItem>
                  <MenuItem value="storage">Depo</MenuItem>
                  <MenuItem value="parking">Otopark</MenuItem>
                  <MenuItem value="other">Diğer</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Cephe"
                  value={unit.facade}
                  onChange={(event) => updateRow("units", unit.client_id, "facade", event.target.value)}
                >
                  <MenuItem value="">Belirtilmedi</MenuItem>
                  {FACADES.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
                <NumberField
                  label="Net Alan"
                  value={unit.net_area}
                  onChange={(value) => updateRow("units", unit.client_id, "net_area", value)}
                  suffix="m²"
                  min={0}
                />
                <NumberField
                  label="Brüt Alan"
                  value={unit.gross_area}
                  onChange={(value) => updateRow("units", unit.client_id, "gross_area", value)}
                  suffix="m²"
                  min={0}
                />
                <NumberField
                  label={`m² Satış Değeri (${currency})`}
                  value={unit.price_per_m2}
                  onChange={(value) => updateRow("units", unit.client_id, "price_per_m2", value)}
                  min={0}
                />
                <NumberField
                  label="Değer Katsayısı"
                  value={unit.value_coefficient}
                  onChange={(value) => updateRow("units", unit.client_id, "value_coefficient", value)}
                  helperText="Kat, cephe ve manzara etkisi"
                  min={0}
                />
                <NumberField
                  label={`Elle Girilen Değer (${currency})`}
                  value={unit.manual_value}
                  onChange={(value) => updateRow("units", unit.client_id, "manual_value", value)}
                  helperText="Girildiğinde m² hesabının yerine geçer"
                  min={0}
                />
                <TextField
                  fullWidth
                  label="Arsa Payı Pay"
                  value={unit.land_share_numerator}
                  onChange={(event) =>
                    updateRow("units", unit.client_id, "land_share_numerator", event.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label="Arsa Payı Payda"
                  value={unit.land_share_denominator}
                  onChange={(event) =>
                    updateRow("units", unit.client_id, "land_share_denominator", event.target.value)
                  }
                />
              </FieldGrid>
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.3,
                  borderRadius: 2.5,
                  background: "#F8FAFC",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" color={COLORS.muted}>
                  Hesaplanan bağımsız bölüm değeri
                </Typography>
                <Typography fontWeight={950} color={COLORS.green}>
                  {formatMoney(unitValue(unit, project.costs.average_sales_price_per_m2), currency)}
                </Typography>
              </Box>
            </Paper>
          ))}
          {!project.units.length && (
            <EmptyState
              title="Bağımsız bölüm bulunmuyor"
              description="Kat ve daire oluşturucuyu kullanın veya bağımsız bölümleri elle ekleyin."
            />
          )}
        </Stack>
      </SectionCard>
    </Stack>
  );

  const renderShares = () => (
    <Stack spacing={2}>
      <SectionCard
        icon={GroupsRoundedIcon}
        title="Tapu Hissedarları"
        subtitle="Hukuki hak hesabı kişi sayısına değil, tapudaki pay ve payda oranlarına göre yapılır."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              onClick={distributeEqually}
              disabled={!project.shareholders.length}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
            >
              Eşit Hisse Dağıt
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() =>
                addRow("shareholders", emptyShareholder(project.shareholders.length))
              }
              sx={{
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 900,
                background: COLORS.blue,
              }}
            >
              Hissedar Ekle
            </Button>
          </Stack>
        }
      >
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 3,
            background:
              Math.abs(calculation.totalSharePercent - 100) <= 0.01
                ? COLORS.greenLight
                : COLORS.amberLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {Math.abs(calculation.totalSharePercent - 100) <= 0.01 ? (
              <CheckCircleRoundedIcon sx={{ color: COLORS.green }} />
            ) : (
              <WarningAmberRoundedIcon sx={{ color: COLORS.amber }} />
            )}
            <Typography fontWeight={900}>
              Tapu hisse oranları toplamı
            </Typography>
          </Stack>
          <Typography
            variant="h6"
            fontWeight={950}
            color={
              Math.abs(calculation.totalSharePercent - 100) <= 0.01
                ? COLORS.green
                : COLORS.amber
            }
          >
            {formatPercent(calculation.totalSharePercent)}
          </Typography>
        </Box>

        <Stack spacing={1.5}>
          {project.shareholders.map((shareholder, index) => {
            const color = PARTY_COLORS[toNumber(shareholder.color_index) % PARTY_COLORS.length];
            const percent = shareholderPercent(shareholder);
            return (
              <Paper
                key={shareholder.client_id}
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 3,
                  border: `1px solid ${COLORS.border}`,
                  borderLeft: `6px solid ${color.main}`,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 950,
                        color: color.main,
                        background: color.light,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box minWidth={0}>
                      <Typography fontWeight={950} noWrap>
                        {shareholder.full_name || `${index + 1}. Hissedar`}
                      </Typography>
                      <Typography variant="caption" color={COLORS.muted}>
                        Hesaplanan oran: {formatPercent(percent)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Tooltip title="Hissedarı sil">
                    <IconButton
                      color="error"
                      onClick={() => removeRow("shareholders", shareholder.client_id)}
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <FieldGrid min={170}>
                  <TextField
                    fullWidth
                    label="Ad Soyad / Unvan"
                    value={shareholder.full_name}
                    onChange={(event) =>
                      updateRow(
                        "shareholders",
                        shareholder.client_id,
                        "full_name",
                        event.target.value
                      )
                    }
                  />
                  <TextField
                    fullWidth
                    label="T.C. / Vergi No"
                    value={shareholder.identity_no}
                    onChange={(event) =>
                      updateRow(
                        "shareholders",
                        shareholder.client_id,
                        "identity_no",
                        event.target.value
                      )
                    }
                  />
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={shareholder.phone}
                    onChange={(event) =>
                      updateRow(
                        "shareholders",
                        shareholder.client_id,
                        "phone",
                        event.target.value
                      )
                    }
                  />
                  <NumberField
                    label="Tapu Pay"
                    value={shareholder.share_numerator}
                    onChange={(value) =>
                      updateRow(
                        "shareholders",
                        shareholder.client_id,
                        "share_numerator",
                        value
                      )
                    }
                    min={0}
                  />
                  <NumberField
                    label="Tapu Payda"
                    value={shareholder.share_denominator}
                    onChange={(value) =>
                      updateRow(
                        "shareholders",
                        shareholder.client_id,
                        "share_denominator",
                        value
                      )
                    }
                    min={0}
                  />
                  <NumberField
                    label="Elle Girilen Oran"
                    value={shareholder.manual_percent}
                    onChange={(value) =>
                      updateRow(
                        "shareholders",
                        shareholder.client_id,
                        "manual_percent",
                        value
                      )
                    }
                    suffix="%"
                    helperText="Pay/payda yoksa kullanılır"
                    min={0}
                    max={100}
                  />
                </FieldGrid>
                <TextField
                  fullWidth
                  label="Hissedar Notu"
                  value={shareholder.notes}
                  onChange={(event) =>
                    updateRow(
                      "shareholders",
                      shareholder.client_id,
                      "notes",
                      event.target.value
                    )
                  }
                  sx={{ mt: 1.5 }}
                />
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 2.5,
                    background: color.light,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 1,
                  }}
                >
                  <DataRow label="Tapu oranı" value={formatPercent(percent)} strong color={color.main} />
                  <DataRow
                    label="Hak edilen alan"
                    value={formatArea(calculation.ownersAreaEntitlement * (percent / 100))}
                    strong
                  />
                  <DataRow
                    label="Hak edilen değer"
                    value={formatMoney(calculation.ownersValueEntitlement * (percent / 100), currency)}
                    strong
                  />
                </Box>
              </Paper>
            );
          })}
          {!project.shareholders.length && (
            <EmptyState
              title="Hissedar eklenmedi"
              description="Tapudaki tüm maliklerin pay ve payda bilgilerini eksiksiz ekleyin."
              action={
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => addRow("shareholders", emptyShareholder(0))}
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                >
                  İlk Hissedarı Ekle
                </Button>
              }
            />
          )}
        </Stack>
      </SectionCard>

      <SectionCard
        icon={BusinessRoundedIcon}
        title="Kat Karşılığı Anlaşması"
        subtitle="Müteahhit ve arsa sahipleri arasındaki temel paylaşım şartları"
      >
        <FieldGrid>
          <TextField
            fullWidth
            label="Müteahhit / Firma"
            value={project.agreement.contractor_name}
            onChange={(event) =>
              updateSection("agreement", "contractor_name", event.target.value)
            }
          />
          <NumberField
            label="Müteahhit Oranı"
            value={project.agreement.contractor_rate}
            onChange={(value) => updateSection("agreement", "contractor_rate", value)}
            suffix="%"
            min={0}
            max={100}
          />
          <TextField
            fullWidth
            label="Arsa Sahipleri Oranı"
            value={formatPercent(calculation.ownersRate)}
            InputProps={{ readOnly: true }}
            helperText="Müteahhit oranından otomatik hesaplanır"
          />
          <TextField
            select
            fullWidth
            label="Paylaşım Esası"
            value={project.agreement.allocation_basis}
            onChange={(event) =>
              updateSection("agreement", "allocation_basis", event.target.value)
            }
          >
            <MenuItem value="value">Ekonomik değere göre</MenuItem>
            <MenuItem value="area">Brüt metrekareye göre</MenuItem>
            <MenuItem value="unit">Bağımsız bölüm adedine göre</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            label="Ortak Alan Kuralı"
            value={project.agreement.common_area_rule}
            onChange={(event) =>
              updateSection("agreement", "common_area_rule", event.target.value)
            }
          >
            <MenuItem value="included">Paylaşım alanına dahil</MenuItem>
            <MenuItem value="excluded">Paylaşım alanına dahil değil</MenuItem>
            <MenuItem value="proportional">Oransal dağıtılır</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            label="Para Birimi"
            value={project.agreement.currency}
            onChange={(event) => updateSection("agreement", "currency", event.target.value)}
          >
            {CURRENCIES.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="date"
            label="Anlaşma Tarihi"
            value={project.agreement.agreement_date || ""}
            onChange={(event) =>
              updateSection("agreement", "agreement_date", event.target.value)
            }
            InputLabelProps={{ shrink: true }}
          />
        </FieldGrid>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Anlaşma ve Paylaşım Notları"
          value={project.agreement.notes}
          onChange={(event) => updateSection("agreement", "notes", event.target.value)}
          sx={{ mt: 1.5 }}
        />
      </SectionCard>
    </Stack>
  );

  const renderAllocation = () => (
    <Stack spacing={2}>
      <SectionCard
        icon={SwapHorizRoundedIcon}
        title="Müteahhit ve Arsa Sahibi Havuzları"
        subtitle="Anlaşma oranına göre teorik haklar ile fiilen verilen bağımsız bölümleri karşılaştırır."
        action={
          <Button
            variant="contained"
            startIcon={<AutoAwesomeRoundedIcon />}
            onClick={() => setAllocationDialog(true)}
            disabled={!project.units.length || !project.shareholders.length}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 900,
              background: COLORS.orange,
              "&:hover": { background: COLORS.orangeDark },
            }}
          >
            Otomatik Paylaştır
          </Button>
        }
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: `1px solid ${COLORS.orange}35`, background: COLORS.orangeLight }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={1.5}>
              <Box>
                <Typography fontWeight={950} color={COLORS.orangeDark}>
                  {project.agreement.contractor_name || project.contractor_company || "Müteahhit"}
                </Typography>
                <Typography variant="caption" color={COLORS.muted}>
                  Sözleşme oranı {formatPercent(calculation.contractorRate)}
                </Typography>
              </Box>
              <Chip
                label={`${calculation.contractorAssignedUnits.length} bölüm`}
                sx={{ fontWeight: 900, color: COLORS.orangeDark, background: "white" }}
              />
            </Stack>
            <DataRow label="Hak edilen brüt alan" value={formatArea(calculation.contractorAreaEntitlement)} strong />
            <DataRow label="Verilen brüt alan" value={formatArea(calculation.contractorActualArea)} strong />
            <DataRow
              label="Alan farkı"
              value={
                <DifferenceChip
                  value={calculation.contractorActualArea - calculation.contractorAreaEntitlement}
                />
              }
            />
            <Divider sx={{ my: 0.6 }} />
            <DataRow
              label="Hak edilen değer"
              value={formatMoney(calculation.contractorValueEntitlement, currency)}
              strong
            />
            <DataRow
              label="Verilen değer"
              value={formatMoney(calculation.contractorActualValue, currency)}
              strong
            />
            <DataRow
              label="Değer farkı"
              value={
                <DifferenceChip
                  value={calculation.contractorActualValue - calculation.contractorValueEntitlement}
                  mode="money"
                  currency={currency}
                />
              }
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: `1px solid ${COLORS.blue}35`, background: COLORS.blueLight }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={1.5}>
              <Box>
                <Typography fontWeight={950} color={COLORS.blue}>
                  Arsa Sahipleri Toplamı
                </Typography>
                <Typography variant="caption" color={COLORS.muted}>
                  Sözleşme oranı {formatPercent(calculation.ownersRate)}
                </Typography>
              </Box>
              <Chip
                label={`${project.units.filter((unit) =>
                  project.shareholders.some(
                    (shareholder) => unitPartyRatio(unit, shareholder.client_id) > 0
                  )
                ).length} bölüm`}
                sx={{ fontWeight: 900, color: COLORS.blue, background: "white" }}
              />
            </Stack>
            <DataRow label="Hak edilen brüt alan" value={formatArea(calculation.ownersAreaEntitlement)} strong />
            <DataRow
              label="Verilen brüt alan"
              value={formatArea(sumBy(calculation.partyResults, (item) => item.actualArea))}
              strong
            />
            <DataRow
              label="Alan farkı"
              value={
                <DifferenceChip
                  value={
                    sumBy(calculation.partyResults, (item) => item.actualArea) -
                    calculation.ownersAreaEntitlement
                  }
                />
              }
            />
            <Divider sx={{ my: 0.6 }} />
            <DataRow
              label="Hak edilen değer"
              value={formatMoney(calculation.ownersValueEntitlement, currency)}
              strong
            />
            <DataRow
              label="Verilen değer"
              value={formatMoney(sumBy(calculation.partyResults, (item) => item.actualValue), currency)}
              strong
            />
            <DataRow
              label="Değer farkı"
              value={
                <DifferenceChip
                  value={
                    sumBy(calculation.partyResults, (item) => item.actualValue) -
                    calculation.ownersValueEntitlement
                  }
                  mode="money"
                  currency={currency}
                />
              }
            />
          </Paper>
        </Box>
      </SectionCard>

      <SectionCard
        icon={GroupsRoundedIcon}
        title="Hissedar Bazında Hak ve Denge"
        subtitle="Pozitif fark fazla, negatif fark eksik tahsisi gösterir."
      >
        <Stack spacing={1.5}>
          {calculation.partyResults.map((result, index) => {
            const color = PARTY_COLORS[
              toNumber(result.shareholder.color_index) % PARTY_COLORS.length
            ];
            return (
              <Paper
                key={result.shareholder.client_id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${COLORS.border}`,
                  borderLeft: `6px solid ${color.main}`,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  gap={2}
                >
                  <Box minWidth={180}>
                    <Typography fontWeight={950} color={COLORS.text}>
                      {result.shareholder.full_name || `${index + 1}. Hissedar`}
                    </Typography>
                    <Typography variant="caption" color={COLORS.muted}>
                      Tapu oranı {formatPercent(result.percent)} · {result.assignedUnits.length} bölüm
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AllocationProgress
                      label="Alan gerçekleşmesi"
                      actual={result.actualArea}
                      target={result.entitledArea}
                      color={color.main}
                    />
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <DifferenceChip value={result.areaDifference} />
                    <DifferenceChip value={result.valueDifference} mode="money" currency={currency} />
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
          {!calculation.partyResults.length && (
            <EmptyState
              title="Hissedar sonucu bulunmuyor"
              description="Hissedarları ve tapu oranlarını girdikten sonra kişisel haklar burada hesaplanır."
            />
          )}
        </Stack>
      </SectionCard>

      <SectionCard
        icon={ApartmentRoundedIcon}
        title="Bağımsız Bölüm Tahsisi"
        subtitle="Her daire, dükkan veya ofisi müteahhide ya da ilgili hissedara verin."
      >
        <Stack spacing={1.2}>
          {project.units.map((unit) => {
            const party = partyMeta(project, unit.allocation_party);
            const splitTotal = unitSplitTotal(unit);
            return (
              <Paper
                key={unit.client_id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: `1px solid ${party.main}35`,
                  borderLeft: `6px solid ${party.main}`,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(180px, .65fr) repeat(3, minmax(110px, .35fr)) minmax(220px, .9fr)" },
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography fontWeight={950}>
                      No {unit.unit_no || "-"} · {unit.unit_type}
                    </Typography>
                    <Typography variant="caption" color={COLORS.muted}>
                      {unit.floor_name || "Kat belirtilmedi"} {unit.facade ? `· ${unit.facade}` : ""}
                    </Typography>
                  </Box>
                  <DataRow label="Net" value={formatArea(unit.net_area)} strong />
                  <DataRow label="Brüt" value={formatArea(unit.gross_area)} strong />
                  <DataRow
                    label="Değer"
                    value={formatMoney(
                      unitValue(unit, project.costs.average_sales_price_per_m2),
                      currency
                    )}
                    strong
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Tahsis Edilen Taraf</InputLabel>
                    <Select
                      label="Tahsis Edilen Taraf"
                      value={unit.allocation_party || "unassigned"}
                      onChange={(event) =>
                        updateRow(
                          "units",
                          unit.client_id,
                          "allocation_party",
                          event.target.value
                        )
                      }
                    >
                      <MenuItem value="unassigned">Paylaştırılmadı</MenuItem>
                      <MenuItem value="split">Ortak / Hisseli Paylaşım</MenuItem>
                      <MenuItem value="contractor">
                        {project.agreement.contractor_name || project.contractor_company || "Müteahhit"}
                      </MenuItem>
                      {project.shareholders.map((shareholder, index) => (
                        <MenuItem key={shareholder.client_id} value={String(shareholder.client_id)}>
                          {shareholder.full_name || `${index + 1}. Hissedar`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {unit.allocation_party === "split" && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2.5,
                      background: COLORS.purpleLight,
                      border: `1px solid ${COLORS.purple}28`,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", sm: "center" }}
                      gap={1}
                      mb={1.5}
                    >
                      <Box>
                        <Typography fontWeight={950} color={COLORS.purple}>
                          Ortak Bağımsız Bölüm Payları
                        </Typography>
                        <Typography variant="caption" color={COLORS.muted}>
                          Bu bölüm birden fazla tarafa hisseli olarak verilebilir.
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`Toplam ${formatPercent(splitTotal)}`}
                        sx={{
                          fontWeight: 950,
                          color:
                            Math.abs(splitTotal - 100) <= 0.01
                              ? COLORS.green
                              : COLORS.red,
                          background:
                            Math.abs(splitTotal - 100) <= 0.01
                              ? COLORS.greenLight
                              : COLORS.redLight,
                        }}
                      />
                    </Stack>
                    <FieldGrid min={170}>
                      <NumberField
                        label={
                          project.agreement.contractor_name ||
                          project.contractor_company ||
                          "Müteahhit"
                        }
                        value={
                          (unit.allocation_splits || []).find(
                            (split) => split.party_id === "contractor"
                          )?.percent || ""
                        }
                        onChange={(value) =>
                          updateUnitSplit(unit.client_id, "contractor", value)
                        }
                        suffix="%"
                        min={0}
                        max={100}
                      />
                      {project.shareholders.map((shareholder, index) => (
                        <NumberField
                          key={shareholder.client_id}
                          label={shareholder.full_name || `${index + 1}. Hissedar`}
                          value={
                            (unit.allocation_splits || []).find(
                              (split) =>
                                String(split.party_id) ===
                                String(shareholder.client_id)
                            )?.percent || ""
                          }
                          onChange={(value) =>
                            updateUnitSplit(
                              unit.client_id,
                              shareholder.client_id,
                              value
                            )
                          }
                          suffix="%"
                          min={0}
                          max={100}
                        />
                      ))}
                    </FieldGrid>
                  </Box>
                )}
              </Paper>
            );
          })}
          {!project.units.length && (
            <EmptyState
              title="Paylaştırılacak bölüm bulunmuyor"
              description="Önce Kat ve Daireler bölümünden bağımsız bölümleri oluşturun."
            />
          )}
        </Stack>
      </SectionCard>

      <SectionCard
        icon={BusinessRoundedIcon}
        title="Renkli Bina Paylaşım Şeması"
        subtitle="Tüm bağımsız bölümlerin taraflara göre kat bazlı görünümü"
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
          <Chip
            label={project.agreement.contractor_name || project.contractor_company || "Müteahhit"}
            sx={{ fontWeight: 900, color: COLORS.orangeDark, background: COLORS.orangeLight }}
          />
          {project.shareholders.map((shareholder, index) => {
            const color = PARTY_COLORS[toNumber(shareholder.color_index) % PARTY_COLORS.length];
            return (
              <Chip
                key={shareholder.client_id}
                label={shareholder.full_name || `${index + 1}. Hissedar`}
                sx={{ fontWeight: 900, color: color.main, background: color.light }}
              />
            );
          })}
          <Chip
            label="Ortak / Hisseli"
            sx={{ fontWeight: 900, color: COLORS.purple, background: COLORS.purpleLight }}
          />
          <Chip label="Paylaştırılmadı" sx={{ fontWeight: 900 }} />
        </Stack>
        <BuildingPreview project={project} />
      </SectionCard>
    </Stack>
  );

  const renderFeasibility = () => {
    const scenarioRates = Array.from(
      new Set([
        clamp(calculation.contractorRate - 5, 0, 100),
        calculation.contractorRate,
        clamp(calculation.contractorRate + 5, 0, 100),
      ])
    );

    return (
      <Stack spacing={2}>
        <SectionCard
          icon={CalculateRoundedIcon}
          title="Maliyet Girdileri"
          subtitle="Müteahhit fizibilitesi için güncel proje ve yapım maliyetlerini girin."
        >
          <FieldGrid>
            <NumberField
              label={`İnşaat Birim Maliyeti (${currency}/m²)`}
              value={project.costs.construction_cost_per_m2}
              onChange={(value) =>
                updateSection("costs", "construction_cost_per_m2", value)
              }
              min={0}
            />
            <NumberField
              label={`Ortalama Satış Değeri (${currency}/m²)`}
              value={project.costs.average_sales_price_per_m2}
              onChange={(value) =>
                updateSection("costs", "average_sales_price_per_m2", value)
              }
              helperText="Bağımsız bölümde özel fiyat yoksa kullanılır"
              min={0}
            />
            <NumberField
              label={`Yıkım Maliyeti (${currency})`}
              value={project.costs.demolition_cost}
              onChange={(value) => updateSection("costs", "demolition_cost", value)}
              min={0}
            />
            <NumberField
              label={`Mimari ve Proje (${currency})`}
              value={project.costs.project_cost}
              onChange={(value) => updateSection("costs", "project_cost", value)}
              min={0}
            />
            <NumberField
              label={`Ruhsat ve İzinler (${currency})`}
              value={project.costs.permit_cost}
              onChange={(value) => updateSection("costs", "permit_cost", value)}
              min={0}
            />
            <NumberField
              label={`Vergi ve Harçlar (${currency})`}
              value={project.costs.tax_fee_cost}
              onChange={(value) => updateSection("costs", "tax_fee_cost", value)}
              min={0}
            />
            <NumberField
              label={`Finansman Gideri (${currency})`}
              value={project.costs.finance_cost}
              onChange={(value) => updateSection("costs", "finance_cost", value)}
              min={0}
            />
            <NumberField
              label={`Pazarlama ve Satış (${currency})`}
              value={project.costs.marketing_cost}
              onChange={(value) => updateSection("costs", "marketing_cost", value)}
              min={0}
            />
            <NumberField
              label={`Diğer Giderler (${currency})`}
              value={project.costs.other_cost}
              onChange={(value) => updateSection("costs", "other_cost", value)}
              min={0}
            />
            <NumberField
              label="Beklenmeyen Gider Payı"
              value={project.costs.contingency_rate}
              onChange={(value) => updateSection("costs", "contingency_rate", value)}
              suffix="%"
              min={0}
              max={100}
            />
          </FieldGrid>
        </SectionCard>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" },
            gap: 1.5,
          }}
        >
          <MetricCard
            label="Toplam Proje Değeri"
            value={formatMoney(calculation.totalProjectValue, currency)}
            icon={AssessmentRoundedIcon}
            color={COLORS.blue}
            light={COLORS.blueLight}
          />
          <MetricCard
            label="Toplam Proje Maliyeti"
            value={formatMoney(calculation.totalCost, currency)}
            icon={CalculateRoundedIcon}
            color={COLORS.red}
            light={COLORS.redLight}
          />
          <MetricCard
            label="Müteahhit Tahmini Geliri"
            value={formatMoney(calculation.contractorRevenue, currency)}
            icon={BusinessRoundedIcon}
            color={COLORS.orange}
            light={COLORS.orangeLight}
          />
          <MetricCard
            label="Tahmini Müteahhit Kârı"
            value={formatMoney(calculation.estimatedProfit, currency)}
            icon={calculation.estimatedProfit >= 0 ? CheckCircleRoundedIcon : WarningAmberRoundedIcon}
            color={calculation.estimatedProfit >= 0 ? COLORS.green : COLORS.red}
            light={calculation.estimatedProfit >= 0 ? COLORS.greenLight : COLORS.redLight}
            hint={`Satış kâr marjı ${formatPercent(calculation.profitMargin)}`}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(320px, .85fr)" },
            gap: 2,
          }}
        >
          <SectionCard
            icon={AssessmentRoundedIcon}
            title="Müteahhit Karlılık Analizi"
            subtitle="Girilen değerler üzerinden otomatik hesaplanır."
          >
            <DataRow
              label="Doğrudan yapım maliyeti"
              value={formatMoney(calculation.directConstructionCost, currency)}
            />
            <DataRow label="Sabit ve diğer giderler" value={formatMoney(calculation.fixedCosts, currency)} />
            <DataRow
              label="Beklenmeyen gider karşılığı"
              value={formatMoney(calculation.contingencyCost, currency)}
            />
            <Divider sx={{ my: 0.6 }} />
            <DataRow label="Toplam maliyet" value={formatMoney(calculation.totalCost, currency)} strong />
            <DataRow
              label="Müteahhit gelir değeri"
              value={formatMoney(calculation.contractorRevenue, currency)}
              strong
            />
            <DataRow
              label="Tahmini kâr / zarar"
              value={formatMoney(calculation.estimatedProfit, currency)}
              strong
              color={calculation.estimatedProfit >= 0 ? COLORS.green : COLORS.red}
            />
            <Divider sx={{ my: 0.6 }} />
            <DataRow label="Satış kâr marjı" value={formatPercent(calculation.profitMargin)} strong />
            <DataRow label="Maliyet üzerinden getiri" value={formatPercent(calculation.returnOnCost)} strong />
            <DataRow
              label="Başabaş müteahhit oranı"
              value={formatPercent(calculation.breakEvenContractorRate)}
              strong
              color={COLORS.blue}
            />
            {calculation.totalProjectValue > 0 && calculation.totalCost > 0 && (
              <Alert
                severity={calculation.estimatedProfit >= 0 ? "success" : "error"}
                sx={{ mt: 2, borderRadius: 2.5 }}
              >
                {calculation.estimatedProfit >= 0
                  ? `Mevcut %${formatNumber(calculation.contractorRate)} paylaşım oranında proje tahmini olarak kârlıdır.`
                  : `Mevcut %${formatNumber(calculation.contractorRate)} paylaşım oranında proje tahmini zarar göstermektedir.`}
              </Alert>
            )}
          </SectionCard>

          <SectionCard
            icon={SettingsSuggestRoundedIcon}
            title="Oran Senaryoları"
            subtitle="Mevcut oranın 5 puan altı ve üstüyle hızlı karşılaştırma"
          >
            <Stack spacing={1.25}>
              {scenarioRates.map((rate) => {
                const revenue = calculation.totalProjectValue * (rate / 100);
                const profit = revenue - calculation.totalCost;
                const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
                const current = Math.abs(rate - calculation.contractorRate) < 0.01;
                return (
                  <Paper
                    key={rate}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      border: `1px solid ${current ? COLORS.blue : COLORS.border}`,
                      background: current ? COLORS.blueLight : "#FAFCFF",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Box>
                        <Typography fontWeight={950} color={current ? COLORS.blue : COLORS.text}>
                          Müteahhit {formatPercent(rate)}
                        </Typography>
                        <Typography variant="caption" color={COLORS.muted}>
                          Kâr marjı {formatPercent(margin)}
                        </Typography>
                      </Box>
                      <Typography
                        fontWeight={950}
                        color={profit >= 0 ? COLORS.green : COLORS.red}
                        textAlign="right"
                      >
                        {formatMoney(profit, currency)}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </SectionCard>
        </Box>
      </Stack>
    );
  };

  const renderReport = () => (
    <Box id="yapipay-report">
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 3, md: 4 },
          border: `1px solid ${COLORS.border}`,
          overflow: "hidden",
          background: "white",
        }}
      >
        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            color: "white",
            background: `linear-gradient(135deg, ${COLORS.blueDeep} 0%, ${COLORS.blue} 68%, ${COLORS.orange} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={2}
          >
            <Box>
              <Typography variant="overline" fontWeight={950} sx={{ opacity: 0.82, letterSpacing: 1.5 }}>
                MESYAT ARSAPRO
              </Typography>
              <Typography variant={mobile ? "h5" : "h4"} fontWeight={950}>
                Arsa Payı ve Kat Karşılığı Fizibilite Raporu
              </Typography>
              <Typography sx={{ opacity: 0.82, mt: 0.8 }}>
                {project.project_name || "Proje adı girilmedi"}
              </Typography>
            </Box>
            <Box textAlign={{ xs: "left", sm: "right" }}>
              <Typography fontWeight={900}>{project.project_no || "Rapor numarası yok"}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {project.report_date || today()}
              </Typography>
              <Box mt={1}>
                <ProjectStatusChip status={project.status} />
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={950} color={COLORS.blue} mb={1}>
                Proje ve Parsel
              </Typography>
              <DataRow label="Proje" value={project.project_name || "-"} strong />
              <DataRow label="Müşteri" value={project.customer_name || "-"} />
              <DataRow label="Mimar / Sorumlu" value={project.architect_name || "-"} />
              <DataRow
                label="Konum"
                value={
                  [project.parcel.neighborhood, project.parcel.district, project.parcel.city]
                    .filter(Boolean)
                    .join(" / ") || "-"
                }
              />
              <DataRow
                label="Ada / Parsel"
                value={`${project.parcel.block_no || "-"} / ${project.parcel.parcel_no || "-"}`}
              />
              <DataRow label="Net parsel alanı" value={formatArea(calculation.netParcelArea)} strong />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={950} color={COLORS.blue} mb={1}>
                İmar ve Yapılaşma
              </Typography>
              <DataRow
                label="Yapı nizamı"
                value={
                  BUILDING_ORDERS.find((item) => item.value === project.zoning.building_order)?.label || "-"
                }
              />
              <DataRow label="TAKS" value={project.zoning.taks || "-"} />
              <DataRow label="KAKS / Emsal" value={project.zoning.kaks || "-"} />
              <DataRow label="Belediye kat sınırı" value={`${calculation.municipalFloorCount || 0} kat`} />
              <DataRow label="Hmax" value={`${formatNumber(project.zoning.hmax)} m`} />
              <DataRow label="Taban oturumu" value={formatArea(calculation.projectedFootprint)} strong />
              <DataRow label="Azami emsal alanı" value={formatArea(calculation.emsalLimit)} strong />
            </Box>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="h6" fontWeight={950} color={COLORS.text} mb={1.5}>
            Alan ve Paylaşım Özeti
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
              gap: 1.2,
              mb: 3,
            }}
          >
            <MetricCard label="Kapalı Alan" value={formatArea(calculation.totalClosedArea)} />
            <MetricCard label="Dağıtılabilir Alan" value={formatArea(calculation.distributableArea)} />
            <MetricCard label="Toplam Bölüm" value={`${project.units.length} adet`} />
            <MetricCard label="Proje Değeri" value={formatMoney(calculation.totalProjectValue, currency)} />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 1.5,
              mb: 3,
            }}
          >
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, background: COLORS.orangeLight }}>
              <Typography fontWeight={950} color={COLORS.orangeDark} mb={1}>
                Müteahhit · {formatPercent(calculation.contractorRate)}
              </Typography>
              <DataRow label="Hak edilen alan" value={formatArea(calculation.contractorAreaEntitlement)} strong />
              <DataRow label="Hak edilen değer" value={formatMoney(calculation.contractorValueEntitlement, currency)} strong />
              <DataRow label="Tahsis edilen bölüm" value={`${calculation.contractorAssignedUnits.length} adet`} />
              <DataRow label="Tahsis edilen alan" value={formatArea(calculation.contractorActualArea)} />
            </Paper>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, background: COLORS.blueLight }}>
              <Typography fontWeight={950} color={COLORS.blue} mb={1}>
                Arsa Sahipleri · {formatPercent(calculation.ownersRate)}
              </Typography>
              <DataRow label="Hak edilen alan" value={formatArea(calculation.ownersAreaEntitlement)} strong />
              <DataRow label="Hak edilen değer" value={formatMoney(calculation.ownersValueEntitlement, currency)} strong />
              <DataRow
                label="Tahsis edilen bölüm"
                value={`${project.units.filter((unit) =>
                  project.shareholders.some(
                    (shareholder) => unitPartyRatio(unit, shareholder.client_id) > 0
                  )
                ).length} adet`}
              />
              <DataRow
                label="Tahsis edilen alan"
                value={formatArea(sumBy(calculation.partyResults, (item) => item.actualArea))}
              />
            </Paper>
          </Box>

          <Typography variant="h6" fontWeight={950} color={COLORS.text} mb={1.5}>
            Hissedar Hak Tablosu
          </Typography>
          <Box sx={{ overflowX: "auto", mb: 3 }}>
            <Box sx={{ minWidth: 820 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr .7fr 1fr 1fr 1fr 1fr",
                  gap: 1,
                  p: 1.2,
                  borderRadius: "12px 12px 0 0",
                  color: "white",
                  background: COLORS.blueDark,
                }}
              >
                {["Hissedar", "Tapu Oranı", "Hak Alanı", "Verilen Alan", "Alan Farkı", "Değer Farkı"].map(
                  (label) => (
                    <Typography key={label} variant="caption" fontWeight={950}>
                      {label}
                    </Typography>
                  )
                )}
              </Box>
              {calculation.partyResults.map((result, index) => (
                <Box
                  key={result.shareholder.client_id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr .7fr 1fr 1fr 1fr 1fr",
                    gap: 1,
                    p: 1.2,
                    borderLeft: `1px solid ${COLORS.border}`,
                    borderRight: `1px solid ${COLORS.border}`,
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Typography variant="body2" fontWeight={900}>
                    {result.shareholder.full_name || `${index + 1}. Hissedar`}
                  </Typography>
                  <Typography variant="body2">{formatPercent(result.percent)}</Typography>
                  <Typography variant="body2">{formatArea(result.entitledArea)}</Typography>
                  <Typography variant="body2">{formatArea(result.actualArea)}</Typography>
                  <Typography variant="body2" color={Math.abs(result.areaDifference) < 0.01 ? COLORS.green : COLORS.amber}>
                    {formatArea(result.areaDifference)}
                  </Typography>
                  <Typography variant="body2" color={Math.abs(result.valueDifference) < 1 ? COLORS.green : COLORS.amber}>
                    {formatMoney(result.valueDifference, currency)}
                  </Typography>
                </Box>
              ))}
              {!calculation.partyResults.length && (
                <Alert severity="warning">Hissedar bilgisi girilmedi.</Alert>
              )}
            </Box>
          </Box>

          <Typography variant="h6" fontWeight={950} color={COLORS.text} mb={1.5}>
            Bina ve Bağımsız Bölüm Paylaşımı
          </Typography>
          <BuildingPreview project={project} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={950} color={COLORS.text} mb={1.5}>
            Müteahhit Fizibilitesi
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
              gap: 1.2,
              mb: 3,
            }}
          >
            <MetricCard label="Toplam Maliyet" value={formatMoney(calculation.totalCost, currency)} />
            <MetricCard label="Müteahhit Geliri" value={formatMoney(calculation.contractorRevenue, currency)} />
            <MetricCard label="Tahmini Kâr/Zarar" value={formatMoney(calculation.estimatedProfit, currency)} />
            <MetricCard label="Kâr Marjı" value={formatPercent(calculation.profitMargin)} />
          </Box>

          {calculation.warnings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={950} color={COLORS.text} mb={1.5}>
                Kontrol ve Uyarılar
              </Typography>
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                <Stack spacing={0.7}>
                  {calculation.warnings.map((warning, index) => (
                    <Typography key={`${warning}_${index}`} variant="body2">
                      • {warning}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            </Box>
          )}

          <Alert severity="info" sx={{ borderRadius: 3 }}>
            Bu çalışma, kullanıcı tarafından girilen bilgilerle hazırlanmış ön fizibilite ve paylaşım
            raporudur. Resmî uygulama öncesinde güncel imar durumu, plan notları, tapu kayıtları,
            mimari proje ve sözleşme hükümleri yetkili uzmanlar tarafından doğrulanmalıdır.
          </Alert>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={1}
            mt={3}
            pt={2}
            borderTop={`1px solid ${COLORS.border}`}
          >
            <Typography variant="caption" color={COLORS.muted}>
              MesYat ArsaPro · Kurumsal Fizibilite Sistemi
            </Typography>
            <Typography variant="caption" color={COLORS.muted}>
              Oluşturma tarihi: {new Date().toLocaleString("tr-TR")}
            </Typography>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 0:
        return renderOverview();
      case 1:
        return renderParcel();
      case 2:
        return renderZoning();
      case 3:
        return renderBuilding();
      case 4:
        return renderShares();
      case 5:
        return renderAllocation();
      case 6:
        return renderFeasibility();
      case 7:
        return renderReport();
      default:
        return renderOverview();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        color: COLORS.text,
        pb: { xs: 11, md: 5 },
      }}
    >
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #yapipay-report, #yapipay-report * { visibility: visible !important; }
          #yapipay-report { position: absolute !important; left: 0; top: 0; width: 100%; }
          .yapipay-no-print { display: none !important; }
          #yapipay-report .MuiPaper-root { box-shadow: none !important; break-inside: avoid; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>

      <Box
        className="yapipay-no-print"
        sx={{
          color: "white",
          background: `linear-gradient(135deg, ${COLORS.blueDeep} 0%, ${COLORS.blueDark} 55%, ${COLORS.blue} 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 360,
            height: 360,
            borderRadius: "50%",
            right: -130,
            top: -220,
            background: `${COLORS.orange}35`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            right: 150,
            bottom: -180,
            background: "rgba(255,255,255,.07)",
          }}
        />
        <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 1.5, sm: 2.5, lg: 4 }, py: { xs: 2, md: 2.8 }, position: "relative" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            gap={2}
          >
            <Stack direction="row" spacing={1.4} alignItems="center" minWidth={0}>
              <Box
                sx={{
                  width: { xs: 46, md: 54 },
                  height: { xs: 46, md: 54 },
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: "white",
                  color: COLORS.blue,
                  boxShadow: "0 12px 28px rgba(0,0,0,.2)",
                  flexShrink: 0,
                }}
              >
                <ArchitectureRoundedIcon fontSize={mobile ? "medium" : "large"} />
              </Box>
              <Box minWidth={0}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Typography variant={mobile ? "h6" : "h5"} fontWeight={950} noWrap>
                    MesYat ArsaPro
                  </Typography>
                  <Chip
                    size="small"
                    icon={
                      serverOnline === true ? (
                        <CloudDoneRoundedIcon />
                      ) : serverOnline === false ? (
                        <CloudOffRoundedIcon />
                      ) : (
                        <CircularProgress size={14} />
                      )
                    }
                    label={
                      serverOnline === true
                        ? "Sunucu bağlı"
                        : serverOnline === false
                          ? "Servis bekleniyor"
                          : "Bağlantı kontrolü"
                    }
                    sx={{
                      color: "white",
                      background: "rgba(255,255,255,.14)",
                      fontWeight: 850,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.76 }} noWrap={!verySmall}>
                  Arsa payı, imar, kat karşılığı ve müteahhit fizibilite sistemi
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                color="inherit"
                variant="outlined"
                startIcon={<HomeWorkRoundedIcon />}
                onClick={() => setProjectsDialog(true)}
                sx={{
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 900,
                  borderColor: "rgba(255,255,255,.4)",
                  "&:hover": { borderColor: "white", background: "rgba(255,255,255,.08)" },
                }}
              >
                Projeler
              </Button>
              {editorOpen && (
                <Button
                  color="inherit"
                  variant="outlined"
                  startIcon={<PrintRoundedIcon />}
                  onClick={printReport}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Rapor
                </Button>
              )}
              {editorOpen && project.id && (
                <Tooltip title="Projeyi sil">
                  <IconButton
                    onClick={() => setDeleteDialog(true)}
                    sx={{
                      color: "white",
                      border: "1px solid rgba(255,255,255,.4)",
                      borderRadius: 2.5,
                      "&:hover": { background: "rgba(220,38,38,.28)" },
                    }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={createNewProject}
                sx={{
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 950,
                  color: "white",
                  background: COLORS.orange,
                  boxShadow: "none",
                  "&:hover": { background: COLORS.orangeDark, boxShadow: "none" },
                }}
              >
                Yeni Proje
              </Button>
              {editorOpen && (
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                  onClick={saveProject}
                  disabled={saving}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 950,
                    color: COLORS.blueDark,
                    background: "white",
                    boxShadow: "none",
                    "&:hover": { background: "#F8FAFC", boxShadow: "none" },
                  }}
                >
                  {saving ? "Kaydediliyor" : dirty ? "Değişiklikleri Kaydet" : "Kaydedildi"}
                </Button>
              )}
            </Stack>
          </Stack>

          {editorOpen && (
            <Stack direction="row" alignItems="center" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
              <ProjectStatusChip status={project.status} />
              <Typography variant="body2" fontWeight={900} noWrap>
                {project.project_name || "Yeni proje"}
              </Typography>
              {project.project_no && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {project.project_no}
                </Typography>
              )}
              {dirty && (
                <Chip
                  size="small"
                  label="Kaydedilmemiş değişiklik"
                  sx={{ color: "white", background: "rgba(249,115,22,.45)", fontWeight: 900 }}
                />
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {editorOpen ? (
        <>
          <Box
            className="yapipay-no-print"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              background: "rgba(255,255,255,.96)",
              backdropFilter: "blur(12px)",
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 0.5, sm: 2, lg: 4 } }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 58,
                  "& .MuiTab-root": {
                    minHeight: 58,
                    minWidth: { xs: 112, md: 130 },
                    textTransform: "none",
                    fontWeight: 850,
                    color: COLORS.muted,
                  },
                  "& .Mui-selected": { color: `${COLORS.blue} !important`, fontWeight: 950 },
                  "& .MuiTabs-indicator": { height: 4, borderRadius: "4px 4px 0 0", background: COLORS.orange },
                }}
              >
                {TABS.map((item) => {
                  const Icon = item.icon;
                  return <Tab key={item.key} icon={<Icon fontSize="small" />} iconPosition="start" label={item.label} />;
                })}
              </Tabs>
            </Box>
          </Box>

          <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 1.25, sm: 2.5, lg: 4 }, py: { xs: 1.5, md: 2.5 } }}>
            {loadingProject ? (
              <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress sx={{ color: COLORS.blue }} />
                  <Typography color={COLORS.muted}>Proje yükleniyor...</Typography>
                </Stack>
              </Box>
            ) : (
              renderActiveTab()
            )}

            <Stack
              className="yapipay-no-print"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={2.5}
              p={1.5}
              borderRadius={3}
              border={`1px solid ${COLORS.border}`}
              bgcolor="white"
            >
              <Button
                startIcon={<KeyboardArrowLeftRoundedIcon />}
                onClick={goPrevious}
                disabled={activeTab === 0}
                sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
              >
                Önceki
              </Button>
              <Typography variant="caption" color={COLORS.muted}>
                {activeTab + 1} / {TABS.length}
              </Typography>
              {activeTab < TABS.length - 1 ? (
                <Button
                  variant="contained"
                  endIcon={<KeyboardArrowRightRoundedIcon />}
                  onClick={goNext}
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900, background: COLORS.blue }}
                >
                  Sonraki
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<PrintRoundedIcon />}
                  onClick={printReport}
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900, background: COLORS.orange }}
                >
                  Yazdır / PDF
                </Button>
              )}
            </Stack>
          </Box>
        </>
      ) : (
        <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 1.25, sm: 2.5, lg: 4 }, py: { xs: 2, md: 3 } }}>
          <SectionCard
            icon={HomeWorkRoundedIcon}
            title="ArsaPro Projeleri"
            subtitle="Kayıtlı fizibilite çalışmalarınızı açın veya yeni bir proje oluşturun."
            action={
              <Button
                startIcon={<RefreshRoundedIcon />}
                onClick={loadProjects}
                disabled={loadingProjects}
                sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
              >
                Yenile
              </Button>
            }
          >
            {loadingProjects ? (
              <Box sx={{ py: 7, display: "grid", placeItems: "center" }}>
                <CircularProgress sx={{ color: COLORS.blue }} />
              </Box>
            ) : projects.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {projects.slice(0, 9).map((item) => {
                  const parcel = item.parcel || {};
                  return (
                    <Paper
                      key={item.id || item.project_id}
                      elevation={0}
                      onClick={() => openProject(item.id || item.project_id)}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${COLORS.border}`,
                        cursor: "pointer",
                        transition: "all .2s ease",
                        "&:hover": { transform: "translateY(-2px)", borderColor: COLORS.blue, boxShadow: "0 12px 30px rgba(11,79,138,.10)" },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box minWidth={0}>
                          <Typography fontWeight={950} noWrap>
                            {item.project_name || "Adsız proje"}
                          </Typography>
                          <Typography variant="caption" color={COLORS.muted} noWrap display="block">
                            {[parcel.district || item.district, parcel.city || item.city].filter(Boolean).join(" / ") || "Konum girilmedi"}
                          </Typography>
                        </Box>
                        <ProjectStatusChip status={item.status} />
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Typography variant="body2" color={COLORS.muted}>
                          Ada/Parsel
                        </Typography>
                        <Typography variant="body2" fontWeight={900}>
                          {parcel.block_no || item.block_no || "-"}/{parcel.parcel_no || item.parcel_no || "-"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" gap={1} mt={0.5}>
                        <Typography variant="body2" color={COLORS.muted}>
                          Güncelleme
                        </Typography>
                        <Typography variant="body2" fontWeight={900}>
                          {String(item.updated_at || item.created_at || "-").slice(0, 10)}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <EmptyState
                title="Henüz ArsaPro projesi yok"
                description="İlk gerçek arsa, imar ve kat karşılığı çalışmanızı oluşturmak için yeni proje başlatın."
                action={
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={createNewProject}
                    sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 950, background: COLORS.orange }}
                  >
                    Yeni Proje Oluştur
                  </Button>
                }
              />
            )}
          </SectionCard>
        </Box>
      )}

      <Dialog
        open={projectsDialog}
        onClose={() => setProjectsDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4, maxHeight: "88vh" } }}
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            Kayıtlı ArsaPro Projeleri
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={createNewProject}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900, background: COLORS.orange }}
            >
              Yeni Proje
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            placeholder="Proje adı, müşteri, il, ilçe, ada veya parsel ara..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            sx={{ mb: 2 }}
          />
          {loadingProjects ? (
            <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={1}>
              {filteredProjects.map((item) => (
                <Paper
                  key={item.id || item.project_id}
                  elevation={0}
                  onClick={() => openProject(item.id || item.project_id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    border: `1px solid ${COLORS.border}`,
                    cursor: "pointer",
                    "&:hover": { borderColor: COLORS.blue, background: COLORS.blueLight },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Box minWidth={0}>
                      <Typography fontWeight={950} noWrap>
                        {item.project_name || "Adsız proje"}
                      </Typography>
                      <Typography variant="caption" color={COLORS.muted} noWrap display="block">
                        {item.project_no || "Dosya no yok"} · {item.customer_name || "Müşteri yok"}
                      </Typography>
                    </Box>
                    <ProjectStatusChip status={item.status} />
                  </Stack>
                </Paper>
              ))}
              {!filteredProjects.length && (
                <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                  Arama ölçütüne uygun proje bulunamadı.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setProjectsDialog(false)} sx={{ fontWeight: 900 }}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Projeyi Sil</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
            <strong>{project.project_name || "Bu proje"}</strong> kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog(false)}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={deleteProject} disabled={saving}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generateDialog}
        onClose={() => setGenerateDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Katları Yeniden Oluştur</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
            Mevcut katlar, bağımsız bölümler ve yapılmış tahsisler silinerek girilen gerçek değerlere göre yeniden oluşturulacak.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGenerateDialog(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={generateStructure} sx={{ background: COLORS.orange }}>
            Yeniden Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={allocationDialog}
        onClose={() => setAllocationDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Otomatik Paylaştırma</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: 2.5, mb: 2 }}>
            Sistem bağımsız bölümleri <strong>{project.agreement.allocation_basis === "area" ? "brüt alana" : project.agreement.allocation_basis === "unit" ? "bölüm adedine" : "ekonomik değere"}</strong> göre tarafların haklarına en yakın biçimde dağıtır.
          </Alert>
          <Typography variant="body2" color={COLORS.muted}>
            Daha önce yapılmış tüm tahsisler yeniden hesaplanır. Sonucu bağımsız bölüm listesinden elle değiştirebilirsiniz.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAllocationDialog(false)}>Vazgeç</Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeRoundedIcon />}
            onClick={applyAutomaticAllocation}
            sx={{ background: COLORS.orange }}
          >
            Hesapla ve Paylaştır
          </Button>
        </DialogActions>
      </Dialog>

      {editorOpen && (
        <Box
          className="yapipay-no-print"
          sx={{
            display: { xs: "flex", md: "none" },
            position: "fixed",
            left: 10,
            right: 10,
            bottom: 10,
            zIndex: 40,
            p: 1,
            borderRadius: 3,
            background: "rgba(255,255,255,.96)",
            backdropFilter: "blur(14px)",
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 14px 40px rgba(15,23,42,.18)",
            gap: 1,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PrintRoundedIcon />}
            onClick={printReport}
            sx={{ borderRadius: 2.3, textTransform: "none", fontWeight: 900 }}
          >
            Rapor
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveRoundedIcon />}
            onClick={saveProject}
            disabled={saving}
            sx={{ borderRadius: 2.3, textTransform: "none", fontWeight: 950, background: COLORS.blue }}
          >
            Kaydet
          </Button>
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={4500}
        onClose={() => setNotification((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification((previous) => ({ ...previous, open: false }))}
          sx={{ borderRadius: 3, fontWeight: 850, maxWidth: "calc(100vw - 24px)" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
